import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

// English only - single language support
export const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧', nativeName: 'English', region: 'global' },
];

// Auto-detect user's preferred language from browser
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Check if we support this language
  const supported = languages.find(l => l.code === langCode);
  return supported ? langCode : 'en';
};

// UI translations for common elements
const translations = {
  en: {
    // Header
    discover: 'Discover',
    howItWorks: 'How It Works',
    successStories: 'Success Stories',
    startCampaign: 'Start a Campaign',
    signIn: 'Sign In',
    signUp: 'Sign Up',
    dashboard: 'Dashboard',
    profile: 'Profile',
    settings: 'Settings',
    logout: 'Log Out',
    
    // Hero
    heroTitle: 'Trusted fundraising for',
    heroTitleHighlight: 'anything you care about',
    heroSubtitle: 'Join our community raising money for medical needs, emergencies, nonprofits, and more with a trusted crowdfunding platform.',
    seeHowItWorks: 'See How It Works',
    searchPlaceholder: 'Search campaigns or causes...',
    totalRaised: 'Total Raised',
    globalDonors: 'Global Donors',
    successRate: 'Success Rate',
    protected100: 'Protected',
    donorGuarantee: 'Donor Guarantee',
    
    // Footer
    charityFundraising: 'Charity Fundraising',
    medicalFundraising: 'Medical Fundraising',
    emergencyFundraising: 'Emergency Fundraising',
    sportsFundraising: 'Sports Fundraising',
    helpCenter: 'Help Center',
    blog: 'Blog',
    press: 'Press',
    aboutUs: 'About Us',
    careers: 'Careers',
    supportUs: 'Support Us',
    contactUs: 'Contact Us',
    communityGuidelines: 'Community Guidelines',
    
    // Auth
    email: 'Email',
    enterEmail: 'Enter your email',
    password: 'Password',
    enterPassword: 'Enter your password',
    forgotPassword: 'Forgot?',
    useDemoAccount: 'Use demo account',
    dontHaveAccount: "Don't have an account?",
    alreadyHaveAccount: 'Already have an account?',
    enterYourName: 'Enter your full name',
    creatingAccount: 'Creating account...',
    countries120: 'Global Reach',
    worldwideReach: 'Worldwide Reach',
    raisedIn2024: 'Raised in 2024',
    
    // Categories
    browseCategories: 'Find a cause you care about',
    browseCategoriesSubtitle: 'Browse campaigns by category and support causes that matter most to you',
    viewAllCategories: 'View all categories',
    
    // Campaign
    donateNow: 'Donate Now',
    shareNow: 'Share Campaign',
    raised: 'raised',
    goal: 'goal',
    donors: 'donors',
    daysLeft: 'days left',
    story: 'Story',
    updates: 'Updates',
    donations: 'Donations',
    milestone: 'Milestone',
    
    // AI Features
    aiFeatures: 'AI-Powered Features',
    translateCampaign: 'Translate Campaign',
    successPredictor: 'Success Predictor',
    trustScore: 'Trust Score',
    
    // Login/Register
    welcomeBack: 'Welcome back',
    signInToContinue: 'Sign in to your account to continue',
    email: 'Email',
    password: 'Password',
    enterEmail: 'Enter your email',
    enterPassword: 'Enter your password',
    rememberMe: 'Remember me',
    forgotPassword: 'Forgot password?',
    signingIn: 'Signing in...',
    noAccount: 'Do not have an account?',
    hasAccount: 'Already have an account?',
    createAccount: 'Create Account',
    agreeTerms: 'By signing in, you agree to our',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    and: 'and',
    
    // Common
    loading: 'Loading...',
    error: 'Error',
    submit: 'Submit',
    cancel: 'Cancel',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    search: 'Search',
    filter: 'Filter',
    sortBy: 'Sort by',
    of: 'of',
    viewDetails: 'View Details',
    learnMore: 'Learn More',
    
    // Featured Campaigns
    featured: 'Featured',
    campaignsMakingImpact: 'Campaigns making an impact',
    featuredSubtitle: 'These verified campaigns are creating real change. Join thousands of donors making a difference.',
    seeAllCampaigns: 'See all campaigns',
    trending: 'Trending',
    verified: 'Verified',
    escrow: 'Escrow',
    supportThisCause: 'Support This Cause',
    
    // Categories
    medical: 'Medical',
    education: 'Education',
    emergency: 'Emergency',
    nonprofit: 'Non-Profit',
    sports: 'Sports',
    research: 'Research',
    startups: 'Startups',
    community: 'Community',
    
    // Why Choose Us
    whyChooseUs: 'Why Choose Us',
    mostTrustedPlatform: 'The Most Trusted Crowdfunding Platform',
    whyChooseSubtitle: "We've built FundTheHope with trust, transparency, and technology at its core. Here's why thousands choose us over other platforms.",
    donorProtection: 'Donor Awareness',
    donorProtectionDesc: 'We provide tools and information to help donors make informed decisions. Donors contribute at their own discretion.',
    securePayments: 'Secure Payments',
    securePaymentsDesc: 'Bank-level 256-bit SSL encryption protects all transactions and personal data.',
    verifiedCampaigns: 'Campaign Verification',
    verifiedCampaignsDesc: 'Campaigns undergo review and creator verification is required for withdrawals.',
    aiPoweredTools: 'AI-Powered Tools',
    aiPoweredToolsDesc: 'Smart recommendations, story writing, image generation, and social media tools powered by advanced AI.',
    multiLanguageSupport: 'Multi-Language Support',
    multiLanguageSupportDesc: 'Reach donors worldwide. Campaign translation in English, Bahasa Malaysia, Chinese, and Tamil.',
    fastFundRelease: 'Fast Fund Release',
    fastFundReleaseDesc: 'Get funds within 24-48 hours once verification is complete. No waiting weeks like other platforms.',
    howWeCompare: 'How We Compare',
    feature: 'Feature',
    otherPlatforms: 'Other Platforms',
    campaigns: 'Campaigns',
    transparentFees: 'Transparent Fees',
    verification: 'Verification',
    languages: 'Languages',
    releaseTime: 'Release Time',
    
    // AI Features Section
    poweredByAI: 'Powered by AI',
    superchargeYourFundraising: 'Supercharge Your Fundraising with AI',
    aiSectionSubtitle: 'Our cutting-edge AI tools help you create compelling campaigns, reach more donors, and maximize your fundraising success.',
    aiStoryWriter: 'AI Story Writer',
    aiStoryWriterDesc: 'Create compelling campaign stories that connect with donors emotionally.',
    aiImageGenerator: 'AI Image Generator',
    aiImageGeneratorDesc: 'Generate professional campaign images that capture attention.',
    smartRecommendations: 'Smart Recommendations',
    smartRecommendationsDesc: 'Get personalized suggestions to optimize your campaign performance.',
    socialMediaGenerator: 'Social Media Generator',
    socialMediaGeneratorDesc: 'Create engaging posts for all platforms with one click.',
    tryItNow: 'Try It Now',
    
    // Recommendations Section
    aiRecommended: 'AI Recommended',
    recommendedCampaigns: 'Recommended Campaigns',
    recommendedSubtitle: 'Campaigns selected by our AI based on impact, urgency, and community support.',
    viewAll: 'View All',
    
    // How It Works
    howItWorksTitle: 'How FundTheHope Works',
    howItWorksSubtitle: 'Start your fundraising journey in three simple steps',
    step1Title: 'Create Your Campaign',
    step1Desc: 'Tell your story, set your goal, and launch in minutes with our easy campaign builder.',
    step2Title: 'Share With Your Network',
    step2Desc: 'Spread the word through social media, email, and messaging to reach potential donors.',
    step3Title: 'Collect Donations',
    step3Desc: 'Receive donations securely and withdraw funds directly to your bank account.',
    getStartedNow: 'Get Started Now',
    
    // Trust Section
    trustedByMillions: 'Trusted Worldwide',
    trustSectionSubtitle: 'Join our growing community helping causes that matter.',
    campaignsFunded: 'Campaigns Funded',
    countriesSupported: 'Countries Supported',
    donorSatisfaction: 'Donor Satisfaction',
    
    // Testimonials
    realStoriesRealImpact: 'Real stories, real impact',
    testimonialsSubtitle: 'Hear from people whose lives have been changed through FundTheHope.',
    
    // Security Features
    bankLevelSecurity: 'Bank-Level Security',
    securitySubtitle: 'Your donations and data are protected by industry-leading security measures.',
    pciCompliant: 'PCI DSS Compliant',
    gdprReady: 'GDPR Ready',
    ssl256Encryption: '256-bit SSL Encryption',
    
    // CTA Section
    readyToMakeDifference: 'Ready to make a difference?',
    ctaSubtitle: 'Start your fundraising campaign today or support causes that matter to you.',
    startFundraising: 'Start Fundraising',
    exploreCampaigns: 'Explore Campaigns',
    
    // Footer
    aboutUs: 'About Us',
    contactUs: 'Contact Us',
    support: 'Support',
    faq: 'FAQ',
    blog: 'Blog',
    careers: 'Careers',
    press: 'Press',
    allRightsReserved: 'All rights reserved',
    quickLinks: 'Quick Links',
    resources: 'Resources',
    legal: 'Legal',
    helpCenter: 'Help Center',
    safetyTips: 'Safety Tips',
    forCharities: 'For Charities',
    successGuide: 'Success Guide',
    trustAndSafety: 'Trust & Safety',
    cookiePolicy: 'Cookie Policy',
    footerTagline: 'Empowering people to help people through trusted crowdfunding.',
    
    // Dashboard Page
    welcomeUser: 'Welcome back',
    dashboardSubtitle: 'Manage your campaigns and track your donations',
    myCampaigns: 'My Campaigns',
    donationHistory: 'Donation History',
    notifications: 'Notifications',
    savedCampaigns: 'Saved Campaigns',
    totalDonated: 'Total Donated',
    campaignsSupported: 'Campaigns Supported',
    activeCampaigns: 'Active Campaigns',
    noCampaigns: 'No campaigns yet',
    createFirstCampaign: 'Create your first campaign',
    noDonations: 'No donations yet',
    noNotifications: 'No notifications',
    viewAllNotifications: 'View All Notifications',
    markAllRead: 'Mark all as read',
    verifyEmail: 'Verify Email',
    verifyEmailDesc: 'Please verify your email to access all features',
    resendVerification: 'Resend Verification',
    
    // Auth Page
    welcomeToFundTheHope: 'Welcome to FundTheHope',
    signInToAccount: 'Sign in to your account',
    createNewAccount: 'Create a new account',
    fullName: 'Full Name',
    enterFullName: 'Enter your full name',
    confirmPassword: 'Confirm Password',
    confirmYourPassword: 'Confirm your password',
    signingUp: 'Creating account...',
    orContinueWith: 'Or continue with',
    continueWithGoogle: 'Continue with Google',
    continueWithFacebook: 'Continue with Facebook',
    continueWithApple: 'Continue with Apple',
    passwordRequirements: 'Password must be at least 8 characters',
    passwordsDoNotMatch: 'Passwords do not match',
    invalidEmail: 'Please enter a valid email',
    
    // Campaign Detail Page
    campaignBy: 'Campaign by',
    shareThisCampaign: 'Share this campaign',
    reportCampaign: 'Report Campaign',
    campaignStory: 'Campaign Story',
    campaignUpdates: 'Updates',
    recentDonations: 'Recent Donations',
    supportersSayThanks: 'Supporters',
    beFirstDonor: 'Be the first to donate',
    anonymousDonor: 'Anonymous',
    donationAmount: 'Donation Amount',
    tipAmount: 'Tip Amount',
    totalAmount: 'Total Amount',
    paymentMethod: 'Payment Method',
    creditCard: 'Credit Card',
    bankTransfer: 'Bank Transfer',
    donateSecurely: 'Donate Securely',
    yourDonationHelps: 'Your donation helps make a difference',
    leaveMessage: 'Leave a message (optional)',
    donateAnonymously: 'Donate anonymously',
    processingDonation: 'Processing donation...',
    donationSuccessful: 'Donation successful!',
    thankYouForDonation: 'Thank you for your generous donation',
    
    // Create Campaign Page
    createCampaign: 'Create Campaign',
    createCampaignSubtitle: 'Tell your story and start raising funds',
    campaignTitle: 'Campaign Title',
    campaignTitlePlaceholder: 'Enter a compelling title',
    campaignCategory: 'Category',
    selectCategory: 'Select a category',
    fundingGoal: 'Funding Goal',
    campaignDuration: 'Campaign Duration',
    days: 'days',
    campaignDescription: 'Campaign Description',
    descriptionPlaceholder: 'Tell your story...',
    uploadImages: 'Upload Images',
    dragAndDrop: 'Drag and drop images here',
    orBrowse: 'or browse',
    maxFileSize: 'Max file size: 5MB',
    publishCampaign: 'Publish Campaign',
    saveDraft: 'Save as Draft',
    previewCampaign: 'Preview',
    
    // Discover Page
    discoverCampaigns: 'Discover Campaigns',
    discoverSubtitle: 'Find and support causes that matter to you',
    allCategories: 'All Categories',
    sortByNewest: 'Newest',
    sortByPopular: 'Most Popular',
    sortByEndingSoon: 'Ending Soon',
    sortByMostFunded: 'Most Funded',
    noResults: 'No campaigns found',
    tryDifferentSearch: 'Try a different search term or filter',
    showingResults: 'Showing results',
    
    // Settings Page
    accountSettings: 'Account Settings',
    personalInfo: 'Personal Information',
    changePassword: 'Change Password',
    notificationSettings: 'Notification Settings',
    privacySettings: 'Privacy Settings',
    connectedAccounts: 'Connected Accounts',
    dangerZone: 'Danger Zone',
    deleteAccount: 'Delete Account',
    deleteAccountWarning: 'This action cannot be undone',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    saveChanges: 'Save Changes',
    updateProfile: 'Update Profile',
    profileUpdated: 'Profile updated successfully',
    passwordChanged: 'Password changed successfully',
    
    // About Page
    aboutFundTheHopeTitle: 'About FundTheHope',
    ourMission: "We're on a mission to democratize fundraising and make it easy for anyone to support the causes they care about.",
    ourStory: 'Our Story',
    ourVision: 'Our Vision',
    ourTeam: 'Our Team',
    ourStoryTitle: 'Our Story',
    ourStoryText: 'FundTheHope was founded with a simple belief: everyone deserves the chance to get help when they need it most. We have built a platform that connects people in need with those who want to help, making fundraising accessible, transparent, and secure.',
    ourValuesTitle: 'Our Values',
    trustAndSafetyValue: 'Trust & Safety',
    trustAndSafetyDesc: 'We verify campaigns and protect donors with our industry-leading security measures.',
    globalReachValue: 'Global Reach',
    globalReachDesc: 'Supporting causes worldwide with multi-currency donations.',
    communityFirstValue: 'Community First',
    communityFirstDesc: 'Our community of donors and campaigners drives everything we do.',
    transparencyValue: 'Transparency',
    transparencyDesc: 'Clear fees, honest reporting, and open communication at every step.',
    ourImpactTitle: 'Our Impact',
    raisedLabel: 'Raised',
    campaignsLabel: 'Campaigns',
    countriesLabel: 'Countries',
    joinOurTeam: 'Join Our Team',
    
    // Contact Page
    getInTouch: 'Get in Touch',
    contactSubtitle: 'We are here to help you',
    yourName: 'Your Name',
    yourEmail: 'Your Email',
    subject: 'Subject',
    message: 'Message',
    sendMessage: 'Send Message',
    messageSent: 'Message sent successfully',
    officeAddress: 'Office Address',
    phoneNumber: 'Phone Number',
    emailAddress: 'Email Address',
    
    // Help Center
    helpCenterTitle: 'Help Center',
    searchHelp: 'Search for help...',
    popularTopics: 'Popular Topics',
    gettingStarted: 'Getting Started',
    managingCampaigns: 'Managing Campaigns',
    donationQuestions: 'Donation Questions',
    accountHelp: 'Account & Security',
    needMoreHelp: 'Need more help?',
    contactSupport: 'Contact Support',
    
    // Messages Page
    messagesTitle: 'Messages',
    newMessage: 'New Message',
    noMessages: 'No messages yet',
    typeMessage: 'Type a message...',
    send: 'Send',
    
    // Wallet Page
    walletTitle: 'Wallet',
    availableBalance: 'Available Balance',
    pendingBalance: 'Pending Balance',
    withdraw: 'Withdraw',
    transactionHistory: 'Transaction History',
    noTransactions: 'No transactions yet',
    withdrawFunds: 'Withdraw Funds',
    addBankAccount: 'Add Bank Account',
    
    // Verification Page
    verifyIdentity: 'Verify Your Identity',
    verificationRequired: 'Verification required to withdraw funds',
    uploadId: 'Upload ID Document',
    uploadSelfie: 'Upload Selfie',
    verificationPending: 'Verification Pending',
    verificationApproved: 'Verification Approved',
    verificationRejected: 'Verification Rejected',
    
    // Error Messages
    somethingWentWrong: 'Something went wrong',
    pleaseRefresh: 'Please refresh the page and try again',
    networkError: 'Network error. Please check your connection',
    sessionExpired: 'Session expired. Please sign in again',
    accessDenied: 'Access denied',
    notFound: 'Page not found',
    goHome: 'Go Home',
    
    // Success Messages
    success: 'Success',
    operationSuccessful: 'Operation completed successfully',
    changesSaved: 'Changes saved',
    
    // Form Validation
    required: 'This field is required',
    invalidFormat: 'Invalid format',
    tooShort: 'Too short',
    tooLong: 'Too long',
    
    // Time
    justNow: 'Just now',
    minutesAgo: 'minutes ago',
    hoursAgo: 'hours ago',
    daysAgo: 'days ago',
    weeksAgo: 'weeks ago',
    monthsAgo: 'months ago',
    
    // Admin
    adminDashboard: 'Admin Dashboard',
    manageUsers: 'Manage Users',
    manageCampaigns: 'Manage Campaigns',
    reports: 'Reports',
    platformStats: 'Platform Statistics',
    kycReview: 'KYC Review',
    pendingReview: 'Pending Review',
    approved: 'Approved',
    rejected: 'Rejected',
    
    // Partners Page
    becomePartner: 'Become a Partner',
    partnerBenefits: 'Partner Benefits',
    partnerTypes: 'Types of Partners',
    applyNow: 'Apply Now',
    
    // Pricing Page
    pricing: 'Pricing',
    freePlatform: 'Free Platform',
    platformFee: 'Platform Fee',
    transactionFee: 'Transaction Fee',
    noPlatformFee: 'No platform fees',
    
    // Blog Page
    latestPosts: 'Latest Posts',
    readMore: 'Read More',
    
    // Careers Page
    openPositions: 'Open Positions',
    applyForPosition: 'Apply for this position',
    
    // Legal Pages
    termsAndConditions: 'Terms and Conditions',
    lastUpdated: 'Last Updated',
    acceptTerms: 'Accept Terms',
    
    // Edit Campaign Page
    editCampaign: 'Edit Campaign',
    enterAmount: 'Enter amount',
    helpKeepPlatformFree: 'Help us keep this platform free for campaigners',
    percentage: 'Percentage',
    customAmount: 'Custom Amount',
    noTip: 'No tip',
    anonymous: 'Anonymous',
    writeMessage: 'Write a supportive message...',
    completeDonation: 'Complete Donation',
    processing: 'Processing...',
    receiptEmail: "We'll send your donation receipt to this email",
    enterYourName: 'Enter your name',
    donateAnonymously: 'Donate anonymously',
    platformTip: 'Platform tip',
    guestDonationInfo: 'No account needed! Donate as a guest securely via Stripe.',
    of: 'of',
    paymentMethod: 'Payment method',
    creditOrDebitCard: 'Credit or debit card',
    recommended: 'Recommended',
    comingSoon: 'Coming Soon',
    yourInformation: 'Your information',
    firstName: 'First name',
    lastName: 'Last name',
    dontDisplayPublicly: "Don't display my name publicly on the fundraiser",
    donationShowAsAnonymous: 'Your donation will show as "Anonymous"',
    getUpdatesFromFundTheHope: 'Get occasional updates from FundTheHope',
    unsubscribeAnytime: 'You may unsubscribe at any time',
    totalDueToday: 'Total due today',
    fundTheHopeProtects: 'FundTheHope protects your donation',
    byDonatingYouAgree: 'By donating, you agree to our',
    and: 'and',
    enterDonation: 'Enter your donation',
    optionalForReceipt: 'optional - for receipt',
    fundTheHopeTip: 'FundTheHope tip',
    internationalFeesMayApply: 'Your total amount will be charged in the fundraiser currency. International transaction fees may apply.',
    updateYourCampaign: 'Update your campaign details',
    pleaseLoginToEdit: 'Please log in to edit campaigns',
    campaignNotFound: 'Campaign not found',
    noPermissionToEdit: "You don't have permission to edit this campaign",
    backToCampaign: 'Back to Campaign',
    backToDashboard: 'Back to Dashboard',
    campaignTitleLabel: 'Campaign Title',
    fundraisingGoal: 'Fundraising Goal ($)',
    yourStory: 'Your Story',
    campaignImage: 'Campaign Image',
    clickToUploadImage: 'Click to upload image',
    editImage: 'Edit Image',
    videoUrlOptional: 'Video URL (Optional)',
    noChangesToSave: 'No changes to save',
    campaignUpdatedReview: 'Campaign updated! Your changes are pending admin review. The campaign will be visible again once approved.',
    campaignUpdatedSuccess: 'Campaign updated successfully!',
    failedToUpdateCampaign: 'Failed to update campaign',
    savingChanges: 'Saving...',
    saveChangesBtn: 'Save Changes',
    
    // Wallet Page
    signInRequired: 'Sign in Required',
    pleaseSignInToWallet: 'Please sign in to access your wallet.',
    myWallet: 'My Wallet',
    manageFundsPayouts: 'Manage your funds and payouts',
    withdrawFundsBtn: 'Withdraw',
    withdrawFundsTitle: 'Withdraw Funds',
    enterAmountToWithdraw: 'Enter the amount to withdraw. Minimum $10.',
    withdrawalAmount: 'Withdrawal Amount',
    minimumWithdrawal: 'Minimum withdrawal is $10',
    insufficientBalance: 'Insufficient balance',
    withdrawalSuccess: 'Withdrawal request submitted! Funds will arrive in 3-5 business days.',
    withdrawalFailed: 'Withdrawal failed',
    completed: 'Completed',
    pending: 'Pending',
    failed: 'Failed',
    totalReceived: 'Total Received',
    totalWithdrawn: 'Total Withdrawn',
    transactions: 'Transactions',
    bankAccounts: 'Bank Accounts',
    transactionHistoryTitle: 'Transaction History',
    allWalletTransactions: 'All your wallet transactions',
    noTransactionsYet: 'No transactions yet',
    transactionsWillAppear: 'Transactions will appear here when you receive donations or make withdrawals',
    manageBankAccounts: 'Manage your payout accounts',
    addAccount: 'Add Account',
    addBankAccountTitle: 'Add Bank Account',
    addBankAccountDesc: 'Add a bank account for receiving payouts',
    accountHolderName: 'Account Holder Name',
    bankName: 'Bank Name',
    accountNumber: 'Account Number',
    routingNumber: 'Routing Number',
    accountType: 'Account Type',
    checking: 'Checking',
    savings: 'Savings',
    default: 'Default',
    setDefault: 'Set Default',
    noBankAccountsAdded: 'No bank accounts added',
    addBankAccountToReceive: 'Add a bank account to receive payouts',
    removeBankAccount: 'Remove this bank account?',
    failedToRemoveBankAccount: 'Failed to remove bank account',
    failedToSetDefault: 'Failed to set default',
    pleaseFillAllFields: 'Please fill all fields',
    failedToAddBankAccount: 'Failed to add bank account',
    
    // Post Update Page
    postCampaignUpdate: 'Post Campaign Update',
    keepDonorsInformed: 'Keep your donors informed about your campaign progress',
    pleaseLoginToPost: 'Please log in to post updates',
    onlyPostToOwnCampaigns: 'You can only post updates to your own campaigns',
    postingUpdateTo: 'Posting update to',
    updateTitle: 'Update Title *',
    updateTitlePlaceholder: 'e.g., Milestone Reached! 50% Funded!',
    updateContent: 'Update Content *',
    updateContentPlaceholder: 'Share your progress, thank your donors, or provide news about your campaign...',
    characters: 'characters',
    addPhotoOptional: 'Add Photo (Optional)',
    clickToAddPhoto: 'Click to add a photo to your update',
    removePhoto: 'Remove Photo',
    posting: 'Posting...',
    postUpdate: 'Post Update',
    updatePostedSuccess: 'Update posted successfully! Your donors will be notified.',
    failedToPostUpdate: 'Failed to post update',
    pleaseEnterUpdateTitle: 'Please enter an update title',
    pleaseEnterUpdateContent: 'Please enter update content',
    
    // About Page
    aboutFundTheHopeTitle: 'About FundTheHope',
    ourStoryTitle: 'Our Story',
    ourStoryText: 'FundTheHope was founded with a simple belief: everyone deserves the chance to get help when they need it most. We have built a platform that connects people in need with those who want to help, making fundraising accessible, transparent, and secure.',
    ourValuesTitle: 'Our Values',
    trustAndSafetyValue: 'Trust & Safety',
    trustAndSafetyDesc: 'We verify campaigns and protect donors with our industry-leading security measures.',
    globalReachValue: 'Global Reach',
    globalReachDesc: 'Supporting causes worldwide with multi-currency donations.',
    communityFirstValue: 'Community First',
    communityFirstDesc: 'Our community of donors and campaigners drives everything we do.',
    transparencyValue: 'Transparency',
    transparencyDesc: 'Clear fees, honest reporting, and open communication at every step.',
    ourImpactTitle: 'Our Impact',
    raisedLabel: 'Raised',
    campaignsLabel: 'Campaigns',
    countriesLabel: 'Countries',
    
    // Pricing Page
    simplePricing: 'Simple, Transparent Pricing',
    noHiddenFees: 'No hidden fees. Keep more of what you raise.',
    freeToStart: 'Free to start a campaign',
    noPlatformFeesOnDonations: 'No platform fees on donations',
    paymentProcessingFee: 'Payment processing: 2.9% + $0.30',
    instantWithdrawals: 'Instant withdrawals available',
    startYourCampaign: 'Start Your Campaign',
    
    // How It Works Page
    trustTransparency: 'Trust & Transparency',
    successStoriesTitle: 'Success Stories',
    successStoriesComingSoon: 'Success stories coming soon!',
    outcomeLabel: 'Outcome:',
    readyToGetStarted: 'Ready to get started?',
    
    // Common Auth Messages
    failedToLoadCampaign: 'Failed to load campaign',
    
    // Filter Labels
    category: 'Category',
    subcategory: 'Subcategory',
    location: 'Location',
    allCategories: 'All Categories',
    allSubcategories: 'All Subcategories',
    cityOrCountry: 'City or country...',
    selectMultiple: 'Select multiple',
    activeFilters: 'Active filters',
    clearAll: 'Clear All',
    filters: 'Filters',
    trustAndSafetyFilter: 'Trust & Safety',
    goalRange: 'Goal Range',
    country: 'Country',
    allCountries: 'All Countries',
    noCampaignsFound: 'No campaigns found',
    tryDifferentFilters: 'Try adjusting your filters or search terms',
    createFirstCampaign: 'Create the first campaign',
    
    // HeroSection extra
    allCampaignsBtn: 'All Campaigns',
    searchBtn: 'Search',
    
    // Discover Page
    discoverCampaigns: 'Discover Campaigns',
    browseCampaignsSubtitle: 'Browse campaigns and find causes that matter to you',
    campaignsFound: 'campaigns found',
    advancedFilters: 'Advanced Filters',
    
    // Campaign Detail Page
    campaignNotFoundMsg: 'Campaign not found',
    thankYouForDonation: 'Thank you for your donation!',
    contributionMakesDifference: 'Your contribution makes a difference.',
    home: 'Home',
    myCampaigns: 'My Campaigns',
    viewProfile: 'View Profile',
    trustedCollaborators: 'Trusted Collaborators',
    inviteCollaborator: 'Invite Collaborator',
    howYourDonationHelps: 'How Your Donation Helps',
    medicalTreatment: 'Medical treatment and procedures',
    medicationCare: 'Medication and ongoing care',
    hospitalCosts: 'Hospital and facility costs',
    travelTreatment: 'Travel for specialized treatment',
    recoverySupport: 'Recovery and rehabilitation support',
    noUpdatesYet: 'No updates posted yet',
    recent: 'Recent',
    beFirstToDonate: 'Be the first to donate!',
    fundingMilestones: 'Funding Milestones',
    donors: 'Donors',
    daysLeft: 'Days Left',
    makeADonation: 'Make a Donation',
    selectCurrencyLabel: 'Select Currency',
    popular: 'Popular',
    selectAmount: 'Select Amount',
    enterCustomAmount: 'Or Enter Custom Amount',
    tipFundTheHope: 'Tip FundTheHope',
    yourTip: 'Your tip',
    yourNameOptional: 'Your Name (Optional)',
    leaveMessageOptional: 'Leave a Message (Optional)',
    donationSummary: 'Donation Summary',
    yourDonation: 'Your donation',
    total: 'Total',
    live: 'Live',
    noDonationsYet: 'No donations yet',
    donationsWillAppear: 'Donations will appear here once received',
    searchByNameEmail: 'Search by name or email',
    onlyVerifiedUsers: 'Only verified users (email + KYC) can be invited',
    update: 'Update',
    storyTab: 'Story',
    updatesTab: 'Updates',
    donorsTab: 'Donors',
    shareThisCampaign: 'Share this campaign',
    copyLink: 'Copy Link',
    linkCopied: 'Link copied!',
    reportCampaign: 'Report Campaign',
    editCampaign: 'Edit Campaign',
    
    // ZeroFeeBanner
    zeroPlatformFee: '0% Platform Fee',
    donationGoesToCause: 'Your Donation Goes to the Cause',
    zeroFeeSubtitle: 'Unlike other platforms that take 5-10%, we believe every penny should reach those in need. We sustain our mission through optional tips from generous donors like you.',
    noHiddenFeesTitle: 'No Hidden Fees',
    noHiddenFeesDesc: 'Zero platform fees. Only standard payment processing applies (2.9% + $0.30).',
    optionalTipsTitle: 'Optional Tips',
    optionalTipsDesc: 'Support our platform with an optional tip. Every contribution helps us serve more communities.',
    fullTransparencyTitle: 'Full Transparency',
    fullTransparencyDesc: 'See exactly where your money goes. Track every donation with complete transparency.',
    generosityPowersHope: 'Your generosity powers hope. Together, we can make a bigger impact.',
    
    // Create Campaign Page
    whatWouldYouLikeToCreate: 'What would you like to create?',
    chooseBetweenCampaign: 'Choose between a fundraising campaign or a creator project',
    fundraisingCampaign: 'Fundraising Campaign',
    fundraisingForCauses: 'Fundraising for causes & needs',
    bestFor: 'Best for:',
    medicalExpenses: 'Medical expenses',
    emergencyRelief: 'Emergency relief',
    educationFunding: 'Education funding',
    communityProjects: 'Community projects',
    charityOrganizations: 'Charity organizations',
    creatorProject: 'Creator Project',
    buildCreateResearch: 'Build, create & research',
    productDevelopment: 'Product development',
    scientificResearch: 'Scientific research',
    gamesAndApps: 'Games and apps',
    artAndDesign: 'Art and design',
    musicAndFilm: 'Music and film',
    tellYourStory: 'Tell Your Story',
    coverImage: 'Cover Image',
    dragAndDropImage: 'Drag and drop an image, or click to browse',
    imageRequirements: 'PNG, JPG up to 10MB. Recommended: 1200x675px',
    fundraisingGoalLabel: 'Fundraising Goal',
    currencyLabel: 'Currency',
    popularCurrencies: 'Popular Currencies',
    allCurrenciesLabel: 'All Currencies',
    enterTitleFirst: 'Enter title first',
    noEndDate: 'No end date',
    locationLabel: 'Location',
    campaignTitle: 'Campaign Title',
    projectTitle: 'Project Title',
    projectDetails: 'Project Details',
    campaignDetails: 'Campaign Details',
    setupBasicInfo: 'Set up the basic information for your',
    selectCategory: 'Select a category',
    selectSubcategory: 'Select a subcategory',
    selectCategoryFirst: 'Select category first',
    giveCampaignTitle: 'Give your campaign a compelling title',
    giveProjectTitle: 'Give your project a compelling title',
    selectCategorySubcategoryFirst: 'Select category and subcategory first',
    goodTitlesGetMore: 'Be specific and descriptive. Good titles get more donations.',
    selectCurrency: 'Select currency',
    searchCurrency: 'Type to search currency...',
    campaignPerfectFor: 'Perfect for personal causes, emergencies, medical needs, memorials, and charitable fundraising where you need community support.',
    projectPerfectFor: 'Ideal for creators, innovators, researchers, and entrepreneurs who want to bring their ideas to life.',
    medicalBillsTreatments: 'Medical bills, surgeries & treatments',
    emergencyDisaster: 'Emergency relief & disaster recovery',
    educationScholarships: 'Education fees & scholarships',
    memorialFamily: 'Memorial funds & family support',
    techSoftware: 'Tech products & software development',
    scientificMedicalResearch: 'Scientific & medical research',
    gamesFilmsCreative: 'Games, films & creative content',
    artDesignMusic: 'Art, design & music projects',
    inventionsStartups: 'Inventions & startup ideas',
    campaign: 'Campaign',
    project: 'Project',
    yourStoryLabel: 'Your Story',
    storyPlaceholder: 'Tell potential donors about your cause...',
    characters: 'characters',
    minCharacters: 'min',
    removeImage: 'Remove Image',
    uploadImage: 'Upload Image',
    generateAIImage: 'Generate AI Image',
    shareWhatBuilding: "Share what you're building and how the funds will be used",
    shareCauseMatters: 'Share why this cause matters and how the funds will be used',
    
    // Settings Page Extended
    manageAccountPreferences: 'Manage your account preferences and security',
    profileInformation: 'Profile Information',
    updatePersonalInfo: 'Update your personal information',
    memberId: 'Member ID',
    yourMemberId: 'Your FundTheHope Member ID',
    emailCannotBeChanged: 'Email cannot be changed',
    extendedProfile: 'Extended Profile',
    tellDonorsAboutYourself: 'Tell donors about yourself',
    professionalHeadline: 'Professional Headline',
    bio: 'Bio',
    socialProfiles: 'Social Profiles',
    connectSocialProfiles: 'Connect your social profiles for better trust',
    educationBackground: 'Education Background',
    yourEducationalBackground: 'Your educational background',
    certifications: 'Certifications',
    professionalCertifications: 'Professional certifications and credentials',
    achievements: 'Achievements',
    awardsHonors: 'Awards, honors, and notable accomplishments',
    security: 'Security',
    updatePasswordKeepSecure: 'Update your password to keep your account secure',
    confirmNewPassword: 'Confirm New Password',
    mustBeSixChars: 'Must be at least 6 characters',
    notificationPreferences: 'Notification Preferences',
    chooseNotifications: 'Choose how you want to receive notifications',
    emailNotifications: 'Email Notifications',
    smsNotifications: 'SMS Notifications',
    donationReceived: 'Donation received',
    newsletterPromotions: 'Newsletter & promotions',
    importantUpdates: 'Important updates',
    campaignPreferences: 'Campaign Preferences',
    selectCategoriesInterested: 'Select campaign categories you are interested in. You will receive notifications when new campaigns matching your preferences are posted.',
    enableCampaignNotifications: 'Enable Campaign Notifications',
    receiveNotificationsMatching: 'Receive notifications when campaigns matching your interests are posted',
    selectCategories: 'Select Categories',
    chooseCategoriesToFollow: 'Choose categories you want to follow. You can select multiple categories.',
    howItWorksNotifications: 'How it works',
    notificationStep1: 'When a new campaign is posted in your selected categories, you will be notified',
    notificationStep2: 'Notifications appear in the bell icon at the top of the page',
    notificationStep3: 'You will also receive an email notification (if enabled)',
    notificationStep4: 'Click on a notification to view the campaign directly',
    saveCampaignPreferences: 'Save Campaign Preferences',
    preferencesSaved: 'Campaign preferences saved! You will receive notifications for new campaigns in your selected categories.',
    
    // Dashboard Extended
    accountOverview: 'Account Overview',
    totalRaisedDashboard: 'Total Raised',
    totalDonationsCount: 'Total Donations',
    backersCount: 'Backers',
    allCampaigns: 'All Campaigns',
    active: 'Active',
    savedDrafts: 'Saved Drafts',
    projects: 'Projects',
    backedProjects: 'Backed Projects',
    noCampaignsYet: 'You have not created any campaigns yet',
    startFirstCampaign: 'Start your first campaign',
    noDraftsYet: 'No saved drafts yet',
    noProjectsYet: 'No projects yet',
    noBackedProjectsYet: 'You have not backed any projects yet',
    noNotificationsYet: 'No notifications yet',
    continueEditing: 'Continue Editing',
    viewCampaign: 'View Campaign',
    
    // Admin Dashboard Extended
    superAdminDashboard: 'Super Admin Dashboard',
    platformAdminDashboard: 'Platform Admin Dashboard',
    pendingReviews: 'Pending Reviews',
    totalUsers: 'Total Users',
    totalCampaignsCount: 'Total Campaigns',
    totalDonationsAdmin: 'Total Donations',
    recentActivity: 'Recent Activity',
    userManagement: 'User Management',
    campaignManagement: 'Campaign Management',
    financialOverview: 'Financial Overview',
    systemSettings: 'System Settings',
    viewAllUsers: 'View All Users',
    viewAllCampaigns: 'View All Campaigns',
    deletedCampaigns: 'Deleted Campaigns',
    campaignReviews: 'Campaign Reviews',
    restore: 'Restore',
    selectAll: 'Select All',
    bulkActions: 'Bulk Actions',
    
    // Contact Page
    contactUsTitle: 'Contact Us',
    contactUsSubtitle: "We're here to help. Reach out and we'll respond as soon as we can.",
    sendUsMessage: 'Send us a message',
    messageSentTitle: 'Message Sent!',
    messageSentDesc: "We'll get back to you within 24-48 hours.",
    nameLabel: 'Name',
    emailLabel: 'Email',
    subjectLabel: 'Subject',
    messageLabel: 'Message',
    sendMessageBtn: 'Send Message',
    liveChatHours: 'Available Mon-Fri, 9am-6pm EST',
    responseTime: 'Response Time',
    responseTimeDesc: 'Usually within 24-48 hours',
    
    // Help Center Page
    helpCenterTitle: 'Help Center',
    helpCenterSubtitle: 'Find answers to common questions',
    searchHelpPlaceholder: 'Search for help articles...',
    gettingStartedTitle: 'Getting Started',
    gettingStartedDesc: 'Learn how to create and manage campaigns',
    paymentsTitle: 'Payments & Donations',
    paymentsDesc: 'Payment methods, fees, and withdrawals',
    trustSafetyTitle: 'Trust & Safety',
    trustSafetyDesc: 'Verification, security, and fraud prevention',
    forDonorsTitle: 'For Donors',
    forDonorsDesc: 'How to donate and track contributions',
    campaignSuccessTitle: 'Campaign Success',
    campaignSuccessDesc: 'Tips to reach your funding goal',
    accountSettingsTitle: 'Account Settings',
    accountSettingsDesc: 'Manage your profile and preferences',
    articlesCount: 'articles',
    backToCategories: 'Back to Categories',
    stillNeedHelp: 'Still need help?',
    stillNeedHelpDesc: "Can't find what you're looking for? Our support team is here to assist you.",
    
    // Success Stories Page
    realImpactStories: 'Real Impact, Real Stories',
    successStoriesHeroTitle: 'Success Stories That',
    successStoriesHighlight: 'Inspire',
    successStoriesHeroDesc: 'Discover how our community has come together to make dreams come true. These are real stories from real people whose lives have been transformed.',
    totalRaisedLabel: 'Total Raised',
    successfulCampaignsLabel: 'Successful Campaigns',
    happyDonorsLabel: 'Happy Donors',
    successRateLabel: 'Success Rate',
    featuredSuccessStories: 'Featured Success Stories',
    featuredStoriesDesc: 'Real campaigns from real creators who achieved their fundraising goals with your support.',
    fundedBadge: 'Funded',
    viewStory: 'View Story',
    moreCampaigns: 'More Success Campaigns',
    moreCampaignsDesc: 'Browse through more campaigns that have made an impact in our community.',
    shareYourStory: 'Share Your Story',
    shareStoryHeroTitle: 'Your Success Could Inspire Others',
    shareStoryDesc: "Have you successfully funded your campaign? Share your story and inspire others to start their fundraising journey.",
    shareYourSuccessBtn: 'Share Your Success',
    noFeaturedStories: 'No featured stories yet',
    checkBackSoon: 'Check back soon for inspiring success stories!',
    
    // Blog Page
    blogTitle: 'Blog',
    blogSubtitle: 'Tips, stories, and updates from FundTheHope',
    latestPostsTitle: 'Latest Posts',
    readMoreBtn: 'Read More',
    readTime: 'min read',
    postedOn: 'Posted on',
    noBlogPosts: 'No blog posts yet',
    blogComingSoon: 'Stay tuned for tips, stories, and updates!',
    
    // Careers Page
    careersTitle: 'Careers',
    careersSubtitle: 'Join our mission to help people help people',
    joinOurTeamTitle: 'Join Our Team',
    joinOurTeamDesc: "We're building a platform that connects people in need with those who want to help. Join us in making fundraising accessible, transparent, and secure.",
    whyWorkWithUs: 'Why Work With Us?',
    missionDriven: 'Mission-Driven',
    missionDrivenDesc: 'Every day, your work helps people in need achieve their goals.',
    remoteFirst: 'Remote-First',
    remoteFirstDesc: 'Work from anywhere. We believe in flexibility and trust.',
    growthOpportunity: 'Growth Opportunity',
    growthOpportunityDesc: 'Learn, grow, and take on new challenges as we scale.',
    diverseTeam: 'Diverse Team',
    diverseTeamDesc: 'We celebrate different perspectives and backgrounds.',
    openPositionsTitle: 'Open Positions',
    noOpenPositions: 'No open positions at the moment',
    checkBackPositions: 'Check back soon for new opportunities!',
    applyNowBtn: 'Apply Now',
    locationRemote: 'Remote',
    departmentLabel: 'Department',
    typeLabel: 'Type',
    fullTime: 'Full-time',
    partTime: 'Part-time',
    contract: 'Contract',
  },
  ms: {
    // Header
    discover: 'Terokai',
    howItWorks: 'Cara Ia Berfungsi',
    successStories: 'Kisah Kejayaan',
    startCampaign: 'Mulakan Kempen',
    signIn: 'Log Masuk',
    signUp: 'Daftar',
    dashboard: 'Papan Pemuka',
    profile: 'Profil',
    settings: 'Tetapan',
    logout: 'Log Keluar',
    
    // Hero
    heroTitle: 'Pengumpulan dana dipercayai untuk',
    heroTitleHighlight: 'apa sahaja yang anda pedulikan',
    heroSubtitle: 'Sertai jutaan orang yang mengumpul wang untuk keperluan perubatan, kecemasan, badan bukan kerajaan dan banyak lagi dengan platform crowdfunding paling dipercayai di dunia.',
    seeHowItWorks: 'Lihat Cara Ia Berfungsi',
    searchPlaceholder: 'Cari kempen atau tujuan...',
    totalRaised: 'Jumlah Terkumpul',
    globalDonors: 'Penderma Global',
    successRate: 'Kadar Kejayaan',
    protected100: 'Protected',
    donorGuarantee: 'Jaminan Penderma',
    countries120: 'Global Reach',
    worldwideReach: 'Capaian Sedunia',
    raisedIn2024: 'Terkumpul pada 2024',
    
    // Categories
    browseCategories: 'Cari tujuan yang anda pedulikan',
    browseCategoriesSubtitle: 'Layari kempen mengikut kategori dan sokong tujuan yang paling penting untuk anda',
    viewAllCategories: 'Lihat semua kategori',
    
    // Campaign
    donateNow: 'Derma Sekarang',
    shareNow: 'Kongsi Kempen',
    raised: 'terkumpul',
    goal: 'sasaran',
    donors: 'penderma',
    daysLeft: 'hari lagi',
    story: 'Cerita',
    updates: 'Kemas Kini',
    donations: 'Derma',
    milestone: 'Pencapaian',
    
    // AI Features
    aiFeatures: 'Ciri-ciri Berkuasa AI',
    translateCampaign: 'Terjemah Kempen',
    successPredictor: 'Peramal Kejayaan',
    trustScore: 'Skor Kepercayaan',
    
    // Login/Register
    welcomeBack: 'Selamat kembali',
    signInToContinue: 'Log masuk ke akaun anda untuk meneruskan',
    email: 'Emel',
    password: 'Kata Laluan',
    enterEmail: 'Masukkan emel anda',
    enterPassword: 'Masukkan kata laluan anda',
    rememberMe: 'Ingat saya',
    forgotPassword: 'Lupa kata laluan?',
    signingIn: 'Sedang log masuk...',
    noAccount: 'Tiada akaun?',
    hasAccount: 'Sudah ada akaun?',
    createAccount: 'Buat Akaun',
    agreeTerms: 'Dengan log masuk, anda bersetuju dengan',
    termsOfService: 'Terma Perkhidmatan',
    privacyPolicy: 'Dasar Privasi',
    and: 'dan',
    
    // Common
    loading: 'Memuatkan...',
    error: 'Ralat',
    submit: 'Hantar',
    cancel: 'Batal',
    save: 'Simpan',
    edit: 'Sunting',
    delete: 'Padam',
    search: 'Cari',
    filter: 'Tapis',
    sortBy: 'Susun mengikut',
    of: 'daripada',
    viewDetails: 'Lihat Butiran',
    learnMore: 'Ketahui Lebih Lanjut',
    
    // Featured Campaigns
    featured: 'Pilihan',
    campaignsMakingImpact: 'Kempen yang memberi impak',
    featuredSubtitle: 'Kempen yang disahkan ini mencipta perubahan sebenar. Sertai ribuan penderma yang membuat perbezaan.',
    seeAllCampaigns: 'Lihat semua kempen',
    trending: 'Trending',
    verified: 'Disahkan',
    escrow: 'Escrow',
    supportThisCause: 'Sokong Tujuan Ini',
    
    // Categories
    medical: 'Perubatan',
    education: 'Pendidikan',
    emergency: 'Kecemasan',
    nonprofit: 'Bukan Untung',
    sports: 'Sukan',
    research: 'Penyelidikan',
    startups: 'Permulaan',
    community: 'Komuniti',
    
    // Why Choose Us
    whyChooseUs: 'Mengapa Pilih Kami',
    mostTrustedPlatform: 'Platform Crowdfunding Paling Dipercayai',
    whyChooseSubtitle: 'Kami membina FundTheHope dengan kepercayaan, ketelusan dan teknologi sebagai teras.',
    donorProtection: 'Donor Awareness',
    donorProtectionDesc: 'Make informed donation decisions.',
    securePayments: 'Secure Payments',
    securePaymentsDesc: 'Bank-level encryption protects transactions.',
    verifiedCampaigns: 'Kempen Disahkan',
    verifiedCampaignsDesc: 'Setiap kempen menjalani pengesahan ketat.',
    aiPoweredTools: 'Alat Berkuasa AI',
    aiPoweredToolsDesc: 'Cadangan pintar, penulisan cerita dan alat media sosial.',
    multiLanguageSupport: 'Sokongan Pelbagai Bahasa',
    multiLanguageSupportDesc: 'Capai penderma di seluruh dunia.',
    fastFundRelease: 'Pembebasan Dana Pantas',
    fastFundReleaseDesc: 'Dapatkan dana dalam 24-48 jam.',
    howWeCompare: 'Bagaimana Kami Berbanding',
    feature: 'Ciri',
    otherPlatforms: 'Platform Lain',
    protected: 'Dilindungi',
    milestoneBased: 'Berasaskan Pencapaian',
    verification: 'Pengesahan',
    languages: 'Bahasa',
    releaseTime: 'Masa Pembebasan',
    
    // AI Features Section
    poweredByAI: 'Dikuasakan AI',
    superchargeYourFundraising: 'Tingkatkan Pengumpulan Dana Anda dengan AI',
    aiSectionSubtitle: 'Alat AI canggih kami membantu anda mencipta kempen yang menarik.',
    aiStoryWriter: 'Penulis Cerita AI',
    aiStoryWriterDesc: 'Cipta cerita kempen yang menyentuh hati penderma.',
    aiImageGenerator: 'Penjana Imej AI',
    aiImageGeneratorDesc: 'Jana imej kempen profesional.',
    smartRecommendations: 'Cadangan Pintar',
    smartRecommendationsDesc: 'Dapatkan cadangan peribadi untuk kempen anda.',
    socialMediaGenerator: 'Penjana Media Sosial',
    socialMediaGeneratorDesc: 'Cipta pos menarik untuk semua platform.',
    tryItNow: 'Cuba Sekarang',
    
    // Recommendations Section
    aiRecommended: 'Cadangan AI',
    recommendedCampaigns: 'Kempen Disyorkan',
    recommendedSubtitle: 'Kempen dipilih oleh AI kami berdasarkan impak dan sokongan.',
    viewAll: 'Lihat Semua',
    
    // How It Works
    howItWorksTitle: 'Cara FundTheHope Berfungsi',
    howItWorksSubtitle: 'Mulakan perjalanan pengumpulan dana anda dalam tiga langkah mudah',
    step1Title: 'Cipta Kempen Anda',
    step1Desc: 'Ceritakan kisah anda, tetapkan sasaran dan lancarkan dalam beberapa minit.',
    step2Title: 'Kongsi Dengan Rangkaian Anda',
    step2Desc: 'Sebarkan berita melalui media sosial, emel dan mesej.',
    step3Title: 'Kumpul Derma',
    step3Desc: 'Terima derma dengan selamat dan keluarkan dana ke akaun bank anda.',
    getStartedNow: 'Mula Sekarang',
    
    // Trust Section
    trustedByMillions: 'Dipercayai oleh jutaan di seluruh dunia',
    trustSectionSubtitle: 'Join our growing community helping causes that matter.',
    campaignsFunded: 'Kempen Dibiayai',
    countriesSupported: 'Negara Disokong',
    donorSatisfaction: 'Kepuasan Penderma',
    
    // Testimonials
    realStoriesRealImpact: 'Kisah sebenar, impak sebenar',
    testimonialsSubtitle: 'Dengar daripada orang yang hidupnya telah berubah.',
    
    // Security Features
    bankLevelSecurity: 'Keselamatan Tahap Bank',
    securitySubtitle: 'Derma dan data anda dilindungi.',
    pciCompliant: 'Pematuhan PCI DSS',
    gdprReady: 'Sedia GDPR',
    ssl256Encryption: 'Penyulitan SSL 256-bit',
    
    // CTA Section
    readyToMakeDifference: 'Bersedia untuk membuat perbezaan?',
    ctaSubtitle: 'Mulakan kempen pengumpulan dana anda hari ini.',
    startFundraising: 'Mula Mengumpul Dana',
    exploreCampaigns: 'Terokai Kempen',
    
    // Footer
    aboutUs: 'Tentang Kami',
    contactUs: 'Hubungi Kami',
    support: 'Sokongan',
    faq: 'Soalan Lazim',
    blog: 'Blog',
    careers: 'Kerjaya',
    press: 'Akhbar',
    allRightsReserved: 'Hak cipta terpelihara',
    quickLinks: 'Pautan Pantas',
    resources: 'Sumber',
    legal: 'Undang-undang',
    helpCenter: 'Pusat Bantuan',
    safetyTips: 'Tips Keselamatan',
    forCharities: 'Untuk Badan Amal',
    successGuide: 'Panduan Kejayaan',
    trustAndSafety: 'Kepercayaan & Keselamatan',
    cookiePolicy: 'Dasar Kuki',
    footerTagline: 'Memperkasakan orang untuk membantu orang melalui crowdfunding yang dipercayai.',
  },
  zh: {
    // Header
    discover: '探索',
    howItWorks: '如何运作',
    successStories: '成功故事',
    startCampaign: '发起活动',
    signIn: '登录',
    signUp: '注册',
    dashboard: '仪表板',
    profile: '个人资料',
    settings: '设置',
    logout: '退出',
    
    // Hero
    heroTitle: '值得信赖的筹款平台',
    heroTitleHighlight: '为您关心的一切',
    heroSubtitle: '加入数百万人，为医疗需求、紧急情况、非营利组织等筹集资金，使用世界上最受信赖的众筹平台。',
    seeHowItWorks: '了解如何运作',
    searchPlaceholder: '搜索活动或事业...',
    totalRaised: '筹集总额',
    globalDonors: '全球捐助者',
    successRate: '成功率',
    protected100: 'Protected',
    donorGuarantee: '捐助者保障',
    countries120: 'Global Reach',
    worldwideReach: '全球覆盖',
    raisedIn2024: '2024年筹集',
    
    // Categories
    browseCategories: '找到您关心的事业',
    browseCategoriesSubtitle: '按类别浏览活动，支持对您最重要的事业',
    viewAllCategories: '查看所有类别',
    
    // Campaign
    donateNow: '立即捐款',
    shareNow: '分享活动',
    raised: '已筹集',
    goal: '目标',
    donors: '捐助者',
    daysLeft: '天剩余',
    story: '故事',
    updates: '更新',
    donations: '捐款',
    milestone: '里程碑',
    
    // AI Features
    aiFeatures: 'AI驱动功能',
    translateCampaign: '翻译活动',
    successPredictor: '成功预测',
    trustScore: '信任分数',
    
    // Login/Register
    welcomeBack: '欢迎回来',
    signInToContinue: '登录您的账户以继续',
    email: '电子邮件',
    password: '密码',
    enterEmail: '输入您的电子邮件',
    enterPassword: '输入您的密码',
    rememberMe: '记住我',
    forgotPassword: '忘记密码？',
    signingIn: '登录中...',
    noAccount: '没有账户？',
    hasAccount: '已有账户？',
    createAccount: '创建账户',
    agreeTerms: '登录即表示您同意我们的',
    termsOfService: '服务条款',
    privacyPolicy: '隐私政策',
    and: '和',
    
    // Common
    loading: '加载中...',
    error: '错误',
    submit: '提交',
    cancel: '取消',
    save: '保存',
    edit: '编辑',
    delete: '删除',
    search: '搜索',
    filter: '筛选',
    sortBy: '排序',
    of: '/',
    viewDetails: '查看详情',
    learnMore: '了解更多',
    
    // Featured Campaigns
    featured: '精选',
    campaignsMakingImpact: '产生影响的活动',
    featuredSubtitle: '这些经过验证的活动正在创造真正的改变。',
    seeAllCampaigns: '查看所有活动',
    trending: '热门',
    verified: '已验证',
    escrow: '托管',
    supportThisCause: '支持这个事业',
    
    // Categories
    medical: '医疗',
    education: '教育',
    emergency: '紧急',
    nonprofit: '非营利',
    sports: '体育',
    research: '研究',
    startups: '创业',
    community: '社区',
    
    // Why Choose Us
    whyChooseUs: '为什么选择我们',
    mostTrustedPlatform: '最值得信赖的众筹平台',
    whyChooseSubtitle: '我们以信任、透明和技术为核心构建了FundTheHope。',
    donorProtection: 'Donor Awareness',
    donorProtectionDesc: 'Make informed donation decisions.',
    securePayments: 'Secure Payments',
    securePaymentsDesc: 'Bank-level encryption protects transactions.',
    verifiedCampaigns: '已验证活动',
    verifiedCampaignsDesc: '每个活动都经过严格验证。',
    aiPoweredTools: 'AI驱动工具',
    aiPoweredToolsDesc: '智能推荐、故事写作和社交媒体工具。',
    multiLanguageSupport: '多语言支持',
    multiLanguageSupportDesc: '触达全球捐助者。',
    fastFundRelease: '快速资金释放',
    fastFundReleaseDesc: '里程碑验证后24-48小时内获得资金。',
    howWeCompare: '我们的比较',
    feature: '功能',
    otherPlatforms: '其他平台',
    protected: '受保护',
    milestoneBased: '基于里程碑',
    verification: '验证',
    languages: '语言',
    releaseTime: '释放时间',
    
    // AI Features Section
    poweredByAI: 'AI驱动',
    superchargeYourFundraising: '用AI增强您的筹款',
    aiSectionSubtitle: '我们先进的AI工具帮助您创建引人注目的活动。',
    aiStoryWriter: 'AI故事写手',
    aiStoryWriterDesc: '创建与捐助者产生共鸣的故事。',
    aiImageGenerator: 'AI图像生成器',
    aiImageGeneratorDesc: '生成专业的活动图像。',
    smartRecommendations: '智能推荐',
    smartRecommendationsDesc: '获取个性化建议。',
    socialMediaGenerator: '社交媒体生成器',
    socialMediaGeneratorDesc: '一键创建所有平台的帖子。',
    tryItNow: '立即尝试',
    
    // Recommendations Section
    aiRecommended: 'AI推荐',
    recommendedCampaigns: '推荐活动',
    recommendedSubtitle: '我们的AI根据影响力和支持度选择的活动。',
    viewAll: '查看全部',
    
    // How It Works
    howItWorksTitle: 'FundTheHope如何运作',
    howItWorksSubtitle: '三个简单步骤开始您的筹款之旅',
    step1Title: '创建您的活动',
    step1Desc: '讲述您的故事，设定目标，几分钟内启动。',
    step2Title: '分享到您的网络',
    step2Desc: '通过社交媒体、电子邮件传播。',
    step3Title: '收集捐款',
    step3Desc: '安全接收捐款并提取到银行账户。',
    getStartedNow: '立即开始',
    
    // Trust Section
    trustedByMillions: '全球数百万人信赖',
    trustSectionSubtitle: '加入已帮助筹集超过25亿美元的社区。',
    campaignsFunded: '已资助活动',
    countriesSupported: '支持国家',
    donorSatisfaction: '捐助者满意度',
    
    // Testimonials
    realStoriesRealImpact: '真实故事，真实影响',
    testimonialsSubtitle: '听听生活因FundTheHope而改变的人们的声音。',
    
    // Security Features
    bankLevelSecurity: '银行级安全',
    securitySubtitle: '您的捐款和数据受到保护。',
    pciCompliant: 'PCI DSS合规',
    gdprReady: 'GDPR就绪',
    ssl256Encryption: '256位SSL加密',
    
    // CTA Section
    readyToMakeDifference: '准备好做出改变了吗？',
    ctaSubtitle: '今天就开始您的筹款活动。',
    startFundraising: '开始筹款',
    exploreCampaigns: '探索活动',
    
    // Footer
    aboutUs: '关于我们',
    contactUs: '联系我们',
    support: '支持',
    faq: '常见问题',
    blog: '博客',
    careers: '职业',
    press: '新闻',
    allRightsReserved: '版权所有',
    quickLinks: '快速链接',
    resources: '资源',
    legal: '法律',
    helpCenter: '帮助中心',
    safetyTips: '安全提示',
    forCharities: '慈善机构',
    successGuide: '成功指南',
    trustAndSafety: '信任与安全',
    cookiePolicy: 'Cookie政策',
    footerTagline: '通过可信赖的众筹赋能人们互助。',
  },
  ta: {
    // Header
    discover: 'கண்டறியுங்கள்',
    howItWorks: 'எப்படி வேலை செய்கிறது',
    successStories: 'வெற்றிக் கதைகள்',
    startCampaign: 'பிரச்சாரம் தொடங்கு',
    signIn: 'உள்நுழைய',
    signUp: 'பதிவு செய்க',
    dashboard: 'டாஷ்போர்டு',
    profile: 'சுயவிவரம்',
    settings: 'அமைப்புகள்',
    logout: 'வெளியேறு',
    
    // Hero
    heroTitle: 'நம்பகமான நிதி திரட்டல்',
    heroTitleHighlight: 'நீங்கள் கவலைப்படும் எதற்கும்',
    heroSubtitle: 'மருத்துவ தேவைகள், அவசரநிலைகள், இலாப நோக்கற்ற நிறுவனங்கள் மற்றும் பலவற்றிற்கு பணம் திரட்டும் மில்லியன் கணக்கானவர்களுடன் சேருங்கள்.',
    seeHowItWorks: 'எப்படி வேலை செய்கிறது பாருங்கள்',
    searchPlaceholder: 'பிரச்சாரங்கள் அல்லது காரணங்களைத் தேடுங்கள்...',
    totalRaised: 'மொத்தம் திரட்டியது',
    globalDonors: 'உலகளாவிய நன்கொடையாளர்கள்',
    successRate: 'வெற்றி விகிதம்',
    protected100: 'Protected',
    donorGuarantee: 'நன்கொடையாளர் உத்தரவாதம்',
    countries120: 'Global Reach',
    worldwideReach: 'உலகளாவிய தொடர்பு',
    raisedIn2024: '2024 இல் திரட்டியது',
    
    // Categories
    browseCategories: 'நீங்கள் கவலைப்படும் காரணத்தைக் கண்டறியுங்கள்',
    browseCategoriesSubtitle: 'வகை வாரியாக பிரச்சாரங்களை உலாவி, உங்களுக்கு மிக முக்கியமான காரணங்களை ஆதரியுங்கள்',
    viewAllCategories: 'அனைத்து வகைகளையும் காண்க',
    
    // Campaign
    donateNow: 'இப்போது நன்கொடை',
    shareNow: 'பிரச்சாரத்தை பகிர்',
    raised: 'சேகரிக்கப்பட்டது',
    goal: 'இலக்கு',
    donors: 'நன்கொடையாளர்கள்',
    daysLeft: 'நாட்கள் மீதமுள்ளன',
    story: 'கதை',
    updates: 'புதுப்பிப்புகள்',
    donations: 'நன்கொடைகள்',
    milestone: 'மைல்கல்',
    
    // AI Features
    aiFeatures: 'AI சக்தி வாய்ந்த அம்சங்கள்',
    translateCampaign: 'பிரச்சாரத்தை மொழிபெயர்',
    successPredictor: 'வெற்றி கணிப்பான்',
    trustScore: 'நம்பிக்கை மதிப்பெண்',
    
    // Login/Register
    welcomeBack: 'மீண்டும் வரவேற்கிறோம்',
    signInToContinue: 'தொடர உங்கள் கணக்கில் உள்நுழையவும்',
    email: 'மின்னஞ்சல்',
    password: 'கடவுச்சொல்',
    enterEmail: 'உங்கள் மின்னஞ்சலை உள்ளிடவும்',
    enterPassword: 'உங்கள் கடவுச்சொல்லை உள்ளிடவும்',
    rememberMe: 'என்னை நினைவில் வை',
    forgotPassword: 'கடவுச்சொல் மறந்துவிட்டதா?',
    signingIn: 'உள்நுழைகிறது...',
    noAccount: 'கணக்கு இல்லையா?',
    hasAccount: 'ஏற்கனவே கணக்கு உள்ளதா?',
    createAccount: 'கணக்கை உருவாக்கு',
    agreeTerms: 'உள்நுழைவதன் மூலம், நீங்கள் எங்களின்',
    termsOfService: 'சேவை விதிமுறைகள்',
    privacyPolicy: 'தனியுரிமைக் கொள்கை',
    and: 'மற்றும்',
    
    // Common
    loading: 'ஏற்றுகிறது...',
    error: 'பிழை',
    submit: 'சமர்ப்பி',
    cancel: 'ரத்து செய்',
    save: 'சேமி',
    edit: 'திருத்து',
    delete: 'நீக்கு',
    search: 'தேடு',
    filter: 'வடிகட்டு',
    sortBy: 'வரிசைப்படுத்து',
    of: '/',
    viewDetails: 'விவரங்களைக் காண்க',
    learnMore: 'மேலும் அறிக',
    
    // Featured Campaigns
    featured: 'சிறப்பு',
    campaignsMakingImpact: 'தாக்கத்தை ஏற்படுத்தும் பிரச்சாரங்கள்',
    featuredSubtitle: 'இந்த சரிபார்க்கப்பட்ட பிரச்சாரங்கள் உண்மையான மாற்றத்தை உருவாக்குகின்றன.',
    seeAllCampaigns: 'அனைத்து பிரச்சாரங்களையும் காண்க',
    trending: 'டிரெண்டிங்',
    verified: 'சரிபார்க்கப்பட்டது',
    escrow: 'எஸ்க்ரோ',
    supportThisCause: 'இந்த காரணத்தை ஆதரிக்கவும்',
    medical: 'மருத்துவம்',
    education: 'கல்வி',
    emergency: 'அவசரநிலை',
    nonprofit: 'இலாப நோக்கற்ற',
    sports: 'விளையாட்டு',
    research: 'ஆராய்ச்சி',
    startups: 'ஸ்டார்ட்அப்கள்',
    community: 'சமூகம்',
    whyChooseUs: 'ஏன் எங்களை தேர்வு செய்ய வேண்டும்',
    mostTrustedPlatform: 'மிகவும் நம்பகமான க்ரவுட்ஃபண்டிங் தளம்',
    whyChooseSubtitle: 'நம்பிக்கை மற்றும் வெளிப்படைத்தன்மையுடன் கட்டப்பட்டது.',
    donorProtection: 'Donor Awareness',
    donorProtectionDesc: 'Make informed donation decisions.',
    securePayments: 'Secure Payments',
    securePaymentsDesc: 'Bank-level encryption protects transactions.',
    verifiedCampaigns: 'சரிபார்க்கப்பட்ட பிரச்சாரங்கள்',
    verifiedCampaignsDesc: 'ஒவ்வொரு பிரச்சாரமும் சரிபார்க்கப்படுகிறது.',
    aiPoweredTools: 'AI இயங்கும் கருவிகள்',
    aiPoweredToolsDesc: 'புத்திசாலி பரிந்துரைகள் மற்றும் கருவிகள்.',
    multiLanguageSupport: 'பல மொழி ஆதரவு',
    multiLanguageSupportDesc: 'உலகளவில் நன்கொடையாளர்களை அடையுங்கள்.',
    fastFundRelease: 'விரைவான நிதி வெளியீடு',
    fastFundReleaseDesc: '24-48 மணி நேரத்தில் நிதி பெறுங்கள்.',
    howWeCompare: 'நாங்கள் எப்படி ஒப்பிடுகிறோம்',
    feature: 'அம்சம்',
    otherPlatforms: 'பிற தளங்கள்',
    protected: 'பாதுகாக்கப்பட்டது',
    milestoneBased: 'இலக்கு அடிப்படையில்',
    verification: 'சரிபார்ப்பு',
    languages: 'மொழிகள்',
    releaseTime: 'வெளியீட்டு நேரம்',
    poweredByAI: 'AI இயக்கும்',
    superchargeYourFundraising: 'AI உடன் உங்கள் நிதி திரட்டலை அதிகரிக்கவும்',
    aiSectionSubtitle: 'எங்கள் AI கருவிகள் உங்களுக்கு உதவுகின்றன.',
    aiStoryWriter: 'AI கதை எழுத்தாளர்',
    aiStoryWriterDesc: 'ஈர்க்கும் கதைகளை உருவாக்குங்கள்.',
    aiImageGenerator: 'AI படம் உருவாக்கி',
    aiImageGeneratorDesc: 'தொழில்முறை படங்களை உருவாக்குங்கள்.',
    smartRecommendations: 'புத்திசாலி பரிந்துரைகள்',
    smartRecommendationsDesc: 'தனிப்பயன் ஆலோசனைகளைப் பெறுங்கள்.',
    socialMediaGenerator: 'சமூக ஊடக உருவாக்கி',
    socialMediaGeneratorDesc: 'ஒரே கிளிக்கில் இடுகைகளை உருவாக்குங்கள்.',
    tryItNow: 'இப்போது முயற்சிக்கவும்',
    aiRecommended: 'AI பரிந்துரைக்கப்பட்டது',
    recommendedCampaigns: 'பரிந்துரைக்கப்பட்ட பிரச்சாரங்கள்',
    recommendedSubtitle: 'எங்கள் AI தேர்ந்தெடுத்த பிரச்சாரங்கள்.',
    viewAll: 'அனைத்தையும் காண்க',
    howItWorksTitle: 'FundTheHope எப்படி வேலை செய்கிறது',
    howItWorksSubtitle: 'மூன்று எளிய படிகளில் தொடங்குங்கள்',
    step1Title: 'உங்கள் பிரச்சாரத்தை உருவாக்குங்கள்',
    step1Desc: 'உங்கள் கதையைச் சொல்லுங்கள்.',
    step2Title: 'உங்கள் நெட்வொர்க்குடன் பகிரவும்',
    step2Desc: 'சமூக ஊடகங்கள் மூலம் பரப்புங்கள்.',
    step3Title: 'நன்கொடைகளை சேகரிக்கவும்',
    step3Desc: 'பாதுகாப்பாக நன்கொடைகளைப் பெறுங்கள்.',
    getStartedNow: 'இப்போது தொடங்குங்கள்',
    trustedByMillions: 'உலகளவில் மில்லியன் கணக்கானவர்களால் நம்பப்படுகிறது',
    trustSectionSubtitle: 'Join our growing community helping causes that matter.',
    campaignsFunded: 'நிதியளிக்கப்பட்ட பிரச்சாரங்கள்',
    countriesSupported: 'ஆதரிக்கப்படும் நாடுகள்',
    donorSatisfaction: 'நன்கொடையாளர் திருப்தி',
    realStoriesRealImpact: 'உண்மையான கதைகள், உண்மையான தாக்கம்',
    testimonialsSubtitle: 'மாற்றப்பட்ட வாழ்க்கைகளைப் பற்றி கேளுங்கள்.',
    bankLevelSecurity: 'வங்கி நிலை பாதுகாப்பு',
    securitySubtitle: 'உங்கள் தரவு பாதுகாக்கப்படுகிறது.',
    pciCompliant: 'PCI DSS இணக்கம்',
    gdprReady: 'GDPR தயார்',
    ssl256Encryption: '256-பிட் SSL குறியாக்கம்',
    readyToMakeDifference: 'மாற்றத்தை ஏற்படுத்த தயாரா?',
    ctaSubtitle: 'இன்றே உங்கள் பிரச்சாரத்தைத் தொடங்குங்கள்.',
    startFundraising: 'நிதி திரட்டலைத் தொடங்குங்கள்',
    exploreCampaigns: 'பிரச்சாரங்களை ஆராயுங்கள்',
    
    // Footer
    aboutUs: 'எங்களை பற்றி',
    contactUs: 'எங்களை தொடர்பு கொள்ள',
    support: 'ஆதரவு',
    faq: 'அடிக்கடி கேட்கப்படும் கேள்விகள்',
    blog: 'வலைப்பதிவு',
    careers: 'வேலைவாய்ப்புகள்',
    press: 'செய்தி',
    allRightsReserved: 'அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டவை',
    quickLinks: 'விரைவு இணைப்புகள்',
    resources: 'வளங்கள்',
    legal: 'சட்ட',
    helpCenter: 'உதவி மையம்',
    safetyTips: 'பாதுகாப்பு குறிப்புகள்',
    forCharities: 'அறக்கட்டளைகளுக்கு',
    successGuide: 'வெற்றி வழிகாட்டி',
    trustAndSafety: 'நம்பிக்கை & பாதுகாப்பு',
    cookiePolicy: 'குக்கி கொள்கை',
    footerTagline: 'நம்பகமான க்ரவுட்ஃபண்டிங் மூலம் மக்களை உதவ மக்களை அதிகாரப்படுத்துதல்.',
  },
  fr: {
    // Header
    discover: 'Découvrir',
    howItWorks: 'Comment ça marche',
    successStories: 'Témoignages',
    startCampaign: 'Lancer une campagne',
    signIn: 'Se connecter',
    signUp: 'S\'inscrire',
    dashboard: 'Tableau de bord',
    profile: 'Profil',
    settings: 'Paramètres',
    logout: 'Déconnexion',
    
    // Hero
    heroTitle: 'Collecte de fonds de confiance pour',
    heroTitleHighlight: 'tout ce qui vous tient à cœur',
    heroSubtitle: 'Join our community raising money for medical needs, emergencies, nonprofits, and more.',
    seeHowItWorks: 'Voir comment ça marche',
    searchPlaceholder: 'Rechercher des campagnes...',
    totalRaised: 'Total collecté',
    globalDonors: 'Donateurs mondiaux',
    successRate: 'Taux de réussite',
    protected100: 'Protected',
    donorGuarantee: 'Garantie donateur',
    countries120: 'Global Reach',
    worldwideReach: 'Portée mondiale',
    raisedIn2024: 'Collecté en 2024',
    
    // Categories
    browseCategories: 'Trouvez une cause qui vous tient à cœur',
    browseCategoriesSubtitle: 'Parcourez les campagnes par catégorie et soutenez les causes qui comptent le plus',
    viewAllCategories: 'Voir toutes les catégories',
    
    // Campaign
    donateNow: 'Faire un don',
    shareNow: 'Partager',
    raised: 'collectés',
    goal: 'objectif',
    donors: 'donateurs',
    daysLeft: 'jours restants',
    story: 'Histoire',
    updates: 'Mises à jour',
    donations: 'Dons',
    milestone: 'Étape',
    
    // AI Features
    aiFeatures: 'Fonctionnalités IA',
    translateCampaign: 'Traduire',
    successPredictor: 'Prédicteur de succès',
    trustScore: 'Score de confiance',
    
    // Login/Register
    welcomeBack: 'Bon retour',
    signInToContinue: 'Connectez-vous pour continuer',
    email: 'Email',
    password: 'Mot de passe',
    enterEmail: 'Entrez votre email',
    enterPassword: 'Entrez votre mot de passe',
    rememberMe: 'Se souvenir de moi',
    forgotPassword: 'Mot de passe oublié?',
    signingIn: 'Connexion...',
    noAccount: 'Pas de compte?',
    hasAccount: 'Déjà un compte?',
    createAccount: 'Créer un compte',
    agreeTerms: 'En vous connectant, vous acceptez nos',
    termsOfService: 'Conditions d\'utilisation',
    privacyPolicy: 'Politique de confidentialité',
    and: 'et',
    
    // Common
    loading: 'Chargement...',
    error: 'Erreur',
    submit: 'Soumettre',
    cancel: 'Annuler',
    save: 'Enregistrer',
    edit: 'Modifier',
    delete: 'Supprimer',
    search: 'Rechercher',
    filter: 'Filtrer',
    sortBy: 'Trier par',
    of: 'sur',
    viewDetails: 'Voir les détails',
    learnMore: 'En savoir plus',
    
    // Featured Campaigns
    featured: 'En vedette',
    campaignsMakingImpact: 'Campagnes qui font la différence',
    featuredSubtitle: 'Ces campagnes vérifiées créent un vrai changement.',
    seeAllCampaigns: 'Voir toutes les campagnes',
    trending: 'Tendance',
    verified: 'Vérifié',
    escrow: 'Séquestre',
    supportThisCause: 'Soutenir cette cause',
    medical: 'Médical',
    education: 'Éducation',
    emergency: 'Urgence',
    nonprofit: 'Non lucratif',
    sports: 'Sports',
    research: 'Recherche',
    startups: 'Startups',
    community: 'Communauté',
    whyChooseUs: 'Pourquoi nous choisir',
    mostTrustedPlatform: 'La plateforme de crowdfunding la plus fiable',
    whyChooseSubtitle: 'Nous avons construit FundTheHope avec confiance et transparence.',
    donorProtection: 'Donor Awareness',
    donorProtectionDesc: 'Make informed donation decisions.',
    securePayments: 'Secure Payments',
    securePaymentsDesc: 'Bank-level encryption protects transactions.',
    verifiedCampaigns: 'Campagnes vérifiées',
    verifiedCampaignsDesc: 'Chaque campagne est strictement vérifiée.',
    aiPoweredTools: 'Outils alimentés par IA',
    aiPoweredToolsDesc: 'Recommandations intelligentes et outils sociaux.',
    multiLanguageSupport: 'Support multilingue',
    multiLanguageSupportDesc: 'Atteignez des donateurs dans le monde entier.',
    fastFundRelease: 'Libération rapide des fonds',
    fastFundReleaseDesc: 'Recevez les fonds en 24-48 heures.',
    howWeCompare: 'Comment nous comparons',
    feature: 'Fonctionnalité',
    otherPlatforms: 'Autres plateformes',
    protected: 'Protégé',
    milestoneBased: 'Basé sur les étapes',
    verification: 'Vérification',
    languages: 'Langues',
    releaseTime: 'Délai de libération',
    poweredByAI: 'Alimenté par IA',
    superchargeYourFundraising: 'Boostez votre collecte de fonds avec l\'IA',
    aiSectionSubtitle: 'Nos outils IA vous aident à créer des campagnes convaincantes.',
    aiStoryWriter: 'Rédacteur IA',
    aiStoryWriterDesc: 'Créez des histoires captivantes.',
    aiImageGenerator: 'Générateur d\'images IA',
    aiImageGeneratorDesc: 'Générez des images professionnelles.',
    smartRecommendations: 'Recommandations intelligentes',
    smartRecommendationsDesc: 'Obtenez des suggestions personnalisées.',
    socialMediaGenerator: 'Générateur réseaux sociaux',
    socialMediaGeneratorDesc: 'Créez des posts engageants en un clic.',
    tryItNow: 'Essayer maintenant',
    aiRecommended: 'Recommandé par IA',
    recommendedCampaigns: 'Campagnes recommandées',
    recommendedSubtitle: 'Campagnes sélectionnées par notre IA.',
    viewAll: 'Voir tout',
    howItWorksTitle: 'Comment fonctionne FundTheHope',
    howItWorksSubtitle: 'Commencez en trois étapes simples',
    step1Title: 'Créez votre campagne',
    step1Desc: 'Racontez votre histoire et lancez en quelques minutes.',
    step2Title: 'Partagez avec votre réseau',
    step2Desc: 'Diffusez via les réseaux sociaux et email.',
    step3Title: 'Collectez les dons',
    step3Desc: 'Recevez les dons en toute sécurité.',
    getStartedNow: 'Commencer maintenant',
    trustedByMillions: 'Trusted Worldwide',
    trustSectionSubtitle: 'Rejoignez la communauté qui a aidé à collecter plus de 2,5 milliards $.',
    campaignsFunded: 'Campagnes financées',
    countriesSupported: 'Pays soutenus',
    donorSatisfaction: 'Satisfaction donateurs',
    realStoriesRealImpact: 'Vraies histoires, vrai impact',
    testimonialsSubtitle: 'Découvrez les témoignages de ceux dont la vie a changé.',
    bankLevelSecurity: 'Sécurité bancaire',
    securitySubtitle: 'Vos dons et données sont protégés.',
    pciCompliant: 'Conforme PCI DSS',
    gdprReady: 'Prêt RGPD',
    ssl256Encryption: 'Chiffrement SSL 256 bits',
    readyToMakeDifference: 'Prêt à faire la différence?',
    ctaSubtitle: 'Lancez votre campagne aujourd\'hui.',
    startFundraising: 'Commencer la collecte',
    exploreCampaigns: 'Explorer les campagnes',
    
    // Footer
    aboutUs: 'À propos',
    contactUs: 'Nous contacter',
    support: 'Support',
    faq: 'FAQ',
    blog: 'Blog',
    careers: 'Carrières',
    press: 'Presse',
    allRightsReserved: 'Tous droits réservés',
    quickLinks: 'Liens rapides',
    resources: 'Ressources',
    legal: 'Légal',
    helpCenter: 'Centre d\'aide',
    safetyTips: 'Conseils de sécurité',
    forCharities: 'Pour les associations',
    successGuide: 'Guide du succès',
    trustAndSafety: 'Confiance & Sécurité',
    cookiePolicy: 'Politique cookies',
    footerTagline: 'Permettre aux gens de s\'entraider grâce au crowdfunding.',
    
    // ZeroFeeBanner
    zeroPlatformFee: '0% de frais de plateforme',
    donationGoesToCause: 'Your donation goes to the cause',
    zeroFeeSubtitle: 'Contrairement aux autres plateformes qui prennent 5-10%, nous croyons que chaque centime doit atteindre ceux dans le besoin.',
    noHiddenFeesTitle: 'Pas de frais cachés',
    noHiddenFeesDesc: 'Zéro frais de plateforme. Seuls les frais de traitement de paiement standard s\'appliquent (2.9% + 0.30$).',
    optionalTipsTitle: 'Pourboires optionnels',
    optionalTipsDesc: 'Soutenez notre plateforme avec un pourboire optionnel.',
    fullTransparencyTitle: 'Transparence totale',
    fullTransparencyDesc: 'Voyez exactement où va votre argent.',
    generosityPowersHope: 'Votre générosité alimente l\'espoir. Ensemble, nous pouvons avoir un plus grand impact.',
    
    // Filter Labels
    category: 'Catégorie',
    subcategory: 'Sous-catégorie',
    location: 'Localisation',
    allCategories: 'Toutes les catégories',
    allSubcategories: 'Toutes les sous-catégories',
    cityOrCountry: 'Ville ou pays...',
  },
  es: {
    // Header
    discover: 'Descubrir',
    howItWorks: 'Cómo funciona',
    successStories: 'Historias de éxito',
    startCampaign: 'Iniciar campaña',
    signIn: 'Iniciar sesión',
    signUp: 'Registrarse',
    dashboard: 'Panel',
    profile: 'Perfil',
    settings: 'Configuración',
    logout: 'Cerrar sesión',
    
    // Hero
    heroTitle: 'Recaudación de fondos confiable para',
    heroTitleHighlight: 'todo lo que te importa',
    heroSubtitle: 'Únete a millones que recaudan fondos para necesidades médicas, emergencias, organizaciones sin fines de lucro y más.',
    seeHowItWorks: 'Ver cómo funciona',
    searchPlaceholder: 'Buscar campañas...',
    totalRaised: 'Total recaudado',
    globalDonors: 'Donantes globales',
    successRate: 'Tasa de éxito',
    protected100: 'Protected',
    donorGuarantee: 'Garantía del donante',
    countries120: 'Global Reach',
    worldwideReach: 'Alcance mundial',
    raisedIn2024: 'Recaudado en 2024',
    
    // Categories
    browseCategories: 'Encuentra una causa que te importe',
    browseCategoriesSubtitle: 'Explora campañas por categoría y apoya las causas que más te importan',
    viewAllCategories: 'Ver todas las categorías',
    
    // Campaign
    donateNow: 'Donar ahora',
    shareNow: 'Compartir',
    raised: 'recaudados',
    goal: 'meta',
    donors: 'donantes',
    daysLeft: 'días restantes',
    story: 'Historia',
    updates: 'Actualizaciones',
    donations: 'Donaciones',
    milestone: 'Hito',
    
    // AI Features
    aiFeatures: 'Funciones de IA',
    translateCampaign: 'Traducir',
    successPredictor: 'Predictor de éxito',
    trustScore: 'Puntuación de confianza',
    
    // Login/Register
    welcomeBack: 'Bienvenido de nuevo',
    signInToContinue: 'Inicia sesión para continuar',
    email: 'Correo',
    password: 'Contraseña',
    enterEmail: 'Ingresa tu correo',
    enterPassword: 'Ingresa tu contraseña',
    rememberMe: 'Recuérdame',
    forgotPassword: '¿Olvidaste la contraseña?',
    signingIn: 'Iniciando sesión...',
    noAccount: '¿No tienes cuenta?',
    hasAccount: '¿Ya tienes cuenta?',
    createAccount: 'Crear cuenta',
    agreeTerms: 'Al iniciar sesión, aceptas nuestros',
    termsOfService: 'Términos de servicio',
    privacyPolicy: 'Política de privacidad',
    and: 'y',
    
    // Common
    loading: 'Cargando...',
    error: 'Error',
    submit: 'Enviar',
    cancel: 'Cancelar',
    save: 'Guardar',
    edit: 'Editar',
    delete: 'Eliminar',
    search: 'Buscar',
    filter: 'Filtrar',
    sortBy: 'Ordenar por',
    of: 'de',
    viewDetails: 'Ver detalles',
    learnMore: 'Más información',
    
    // Featured Campaigns
    featured: 'Destacado',
    campaignsMakingImpact: 'Campañas que hacen la diferencia',
    featuredSubtitle: 'Estas campañas verificadas están creando cambio real.',
    seeAllCampaigns: 'Ver todas las campañas',
    trending: 'Tendencia',
    verified: 'Verificado',
    escrow: 'Depósito',
    supportThisCause: 'Apoyar esta causa',
    medical: 'Médico',
    education: 'Educación',
    emergency: 'Emergencia',
    nonprofit: 'Sin fines de lucro',
    sports: 'Deportes',
    research: 'Investigación',
    startups: 'Startups',
    community: 'Comunidad',
    whyChooseUs: 'Por qué elegirnos',
    mostTrustedPlatform: 'La plataforma de crowdfunding más confiable',
    whyChooseSubtitle: 'Construimos FundTheHope con confianza y transparencia.',
    donorProtection: 'Donor Awareness',
    donorProtectionDesc: 'Make informed donation decisions.',
    securePayments: 'Secure Payments',
    securePaymentsDesc: 'Bank-level encryption protects transactions.',
    verifiedCampaigns: 'Campañas verificadas',
    verifiedCampaignsDesc: 'Cada campaña pasa por verificación estricta.',
    aiPoweredTools: 'Herramientas con IA',
    aiPoweredToolsDesc: 'Recomendaciones inteligentes y herramientas sociales.',
    multiLanguageSupport: 'Soporte multilingüe',
    multiLanguageSupportDesc: 'Alcanza donantes en todo el mundo.',
    fastFundRelease: 'Liberación rápida de fondos',
    fastFundReleaseDesc: 'Recibe fondos en 24-48 horas.',
    howWeCompare: 'Cómo nos comparamos',
    feature: 'Característica',
    otherPlatforms: 'Otras plataformas',
    protected: 'Protegido',
    milestoneBased: 'Basado en hitos',
    verification: 'Verificación',
    languages: 'Idiomas',
    releaseTime: 'Tiempo de liberación',
    poweredByAI: 'Impulsado por IA',
    superchargeYourFundraising: 'Potencia tu recaudación con IA',
    aiSectionSubtitle: 'Nuestras herramientas de IA te ayudan a crear campañas efectivas.',
    aiStoryWriter: 'Escritor de historias IA',
    aiStoryWriterDesc: 'Crea historias que conectan con donantes.',
    aiImageGenerator: 'Generador de imágenes IA',
    aiImageGeneratorDesc: 'Genera imágenes profesionales.',
    smartRecommendations: 'Recomendaciones inteligentes',
    smartRecommendationsDesc: 'Obtén sugerencias personalizadas.',
    socialMediaGenerator: 'Generador de redes sociales',
    socialMediaGeneratorDesc: 'Crea posts atractivos con un clic.',
    tryItNow: 'Pruébalo ahora',
    aiRecommended: 'Recomendado por IA',
    recommendedCampaigns: 'Campañas recomendadas',
    recommendedSubtitle: 'Campañas seleccionadas por nuestra IA.',
    viewAll: 'Ver todo',
    howItWorksTitle: 'Cómo funciona FundTheHope',
    howItWorksSubtitle: 'Comienza en tres simples pasos',
    step1Title: 'Crea tu campaña',
    step1Desc: 'Cuenta tu historia y lanza en minutos.',
    step2Title: 'Comparte con tu red',
    step2Desc: 'Difunde a través de redes sociales y email.',
    step3Title: 'Recibe donaciones',
    step3Desc: 'Recibe donaciones de forma segura.',
    getStartedNow: 'Comenzar ahora',
    trustedByMillions: 'Confiado por millones',
    trustSectionSubtitle: 'Join our growing community helping causes that matter.',
    campaignsFunded: 'Campañas financiadas',
    countriesSupported: 'Países apoyados',
    donorSatisfaction: 'Satisfacción del donante',
    realStoriesRealImpact: 'Historias reales, impacto real',
    testimonialsSubtitle: 'Escucha a personas cuyas vidas han cambiado.',
    bankLevelSecurity: 'Seguridad bancaria',
    securitySubtitle: 'Tus donaciones y datos están protegidos.',
    pciCompliant: 'Cumplimiento PCI DSS',
    gdprReady: 'Listo para GDPR',
    ssl256Encryption: 'Cifrado SSL de 256 bits',
    readyToMakeDifference: '¿Listo para hacer la diferencia?',
    ctaSubtitle: 'Comienza tu campaña hoy.',
    startFundraising: 'Comenzar a recaudar',
    exploreCampaigns: 'Explorar campañas',
    
    // Footer
    aboutUs: 'Sobre nosotros',
    contactUs: 'Contáctanos',
    support: 'Soporte',
    faq: 'Preguntas frecuentes',
    blog: 'Blog',
    careers: 'Carreras',
    press: 'Prensa',
    allRightsReserved: 'Todos los derechos reservados',
    quickLinks: 'Enlaces rápidos',
    resources: 'Recursos',
    legal: 'Legal',
    helpCenter: 'Centro de ayuda',
    safetyTips: 'Consejos de seguridad',
    forCharities: 'Para organizaciones',
    successGuide: 'Guía del éxito',
    trustAndSafety: 'Confianza y Seguridad',
    cookiePolicy: 'Política de cookies',
    footerTagline: 'Empoderando a las personas para ayudar a través del crowdfunding.',
    
    // About Page
    aboutFundTheHopeTitle: 'Acerca de FundTheHope',
    ourMission: 'Nuestra misión es democratizar la recaudación de fondos.',
    ourStoryTitle: 'Nuestra Historia',
    ourStoryText: 'FundTheHope se fundó con una simple creencia: todos merecen la oportunidad de obtener ayuda cuando más la necesitan.',
    ourValuesTitle: 'Nuestros Valores',
    trustAndSafetyValue: 'Confianza y Seguridad',
    trustAndSafetyDesc: 'Verificamos campañas y protegemos a los donantes.',
    globalReachValue: 'Alcance Global',
    globalReachDesc: 'Apoyando causas en más de 120 países.',
    communityFirstValue: 'Comunidad Primero',
    communityFirstDesc: 'Nuestra comunidad impulsa todo lo que hacemos.',
    transparencyValue: 'Transparencia',
    transparencyDesc: 'Tarifas claras y comunicación abierta.',
    ourImpactTitle: 'Nuestro Impacto',
    raisedLabel: 'Recaudado',
    campaignsLabel: 'Campañas',
    countriesLabel: 'Países',
    
    // ZeroFeeBanner
    zeroPlatformFee: '0% de tarifa de plataforma',
    donationGoesToCause: 'Your donation goes to the cause',
    zeroFeeSubtitle: 'A diferencia de otras plataformas que cobran 5-10%, creemos que cada centavo debe llegar a quienes lo necesitan.',
    noHiddenFeesTitle: 'Sin tarifas ocultas',
    noHiddenFeesDesc: 'Cero tarifas de plataforma. Solo se aplica el procesamiento de pago estándar.',
    optionalTipsTitle: 'Propinas opcionales',
    optionalTipsDesc: 'Apoya nuestra plataforma con una propina opcional.',
    fullTransparencyTitle: 'Transparencia total',
    fullTransparencyDesc: 'Ve exactamente a dónde va tu dinero.',
    generosityPowersHope: 'Tu generosidad impulsa la esperanza.',
    
    // Filter Labels
    category: 'Categoría',
    subcategory: 'Subcategoría',
    location: 'Ubicación',
    allCategories: 'Todas las categorías',
    allSubcategories: 'Todas las subcategorías',
    cityOrCountry: 'Ciudad o país...',
    
    // Create Campaign Page
    whatWouldYouLikeToCreate: '¿Qué te gustaría crear?',
    chooseBetweenCampaign: 'Elige entre una campaña de recaudación o un proyecto creativo',
    campaign: 'Campaña',
    creatorProject: 'Proyecto Creativo',
    fundraisingForCauses: 'Recaudación para causas',
    buildCreateResearch: 'Construir, crear e investigar',
    campaignPerfectFor: 'Perfecto para causas personales, emergencias, necesidades médicas y recaudación benéfica.',
    projectPerfectFor: 'Ideal para creadores, innovadores e investigadores que quieren dar vida a sus ideas.',
    bestFor: 'Ideal para:',
    medicalBillsTreatments: 'Gastos médicos y tratamientos',
    emergencyDisaster: 'Emergencias y desastres',
    educationScholarships: 'Educación y becas',
    memorialFamily: 'Memoriales y apoyo familiar',
    techSoftware: 'Productos tecnológicos y software',
    scientificMedicalResearch: 'Investigación científica y médica',
    gamesFilmsCreative: 'Juegos, películas y contenido creativo',
    artDesignMusic: 'Proyectos de arte, diseño y música',
    inventionsStartups: 'Inventos e ideas de startups',
    tellYourStory: 'Cuenta tu historia',
    coverImage: 'Imagen de portada',
    dragAndDropImage: 'Arrastra una imagen o haz clic para explorar',
    imageRequirements: 'PNG, JPG hasta 10MB. Recomendado: 1200x675px',
    removeImage: 'Eliminar imagen',
    uploadImage: 'Subir imagen',
    generateAIImage: 'Generar imagen con IA',
    yourStoryLabel: 'Tu historia',
    storyPlaceholder: 'Cuéntales a los donantes sobre tu causa...',
    projectDetails: 'Detalles del proyecto',
    campaignDetails: 'Detalles de la campaña',
    setupBasicInfo: 'Configura la información básica de tu',
    project: 'proyecto',
    selectCategory: 'Selecciona una categoría',
    selectSubcategory: 'Selecciona una subcategoría',
    selectCategoryFirst: 'Selecciona categoría primero',
    projectTitle: 'Título del proyecto',
    campaignTitle: 'Título de la campaña',
    giveProjectTitle: 'Dale a tu proyecto un título atractivo',
    giveCampaignTitle: 'Dale a tu campaña un título atractivo',
    selectCategorySubcategoryFirst: 'Selecciona categoría y subcategoría primero',
    goodTitlesGetMore: 'Sé específico. Los buenos títulos obtienen más donaciones.',
    currencyLabel: 'Moneda',
    selectCurrency: 'Selecciona moneda',
    searchCurrency: 'Buscar moneda...',
    shareWhatBuilding: 'Comparte lo que estás construyendo y cómo se usarán los fondos',
    shareCauseMatters: 'Comparte por qué esta causa importa y cómo se usarán los fondos',
    characters: 'caracteres',
    minCharacters: 'mín',
    
    // Discover Page
    discoverCampaigns: 'Descubrir Campañas',
    browseCampaignsSubtitle: 'Explora campañas y encuentra causas que te importan',
    campaignsFound: 'campañas encontradas',
    advancedFilters: 'Filtros avanzados',
  },
  de: {
    // Header
    discover: 'Entdecken',
    howItWorks: 'So funktioniert es',
    successStories: 'Erfolgsgeschichten',
    startCampaign: 'Kampagne starten',
    signIn: 'Anmelden',
    signUp: 'Registrieren',
    dashboard: 'Dashboard',
    profile: 'Profil',
    settings: 'Einstellungen',
    logout: 'Abmelden',
    
    // Hero
    heroTitle: 'Vertrauenswürdiges Fundraising für',
    heroTitleHighlight: 'alles was Ihnen wichtig ist',
    heroSubtitle: 'Schließen Sie sich Millionen an, die Geld für medizinische Bedürfnisse, Notfälle, gemeinnützige Organisationen und mehr sammeln.',
    seeHowItWorks: 'So funktioniert es',
    searchPlaceholder: 'Kampagnen suchen...',
    totalRaised: 'Gesammelt',
    globalDonors: 'Globale Spender',
    successRate: 'Erfolgsrate',
    protected100: 'Protected',
    donorGuarantee: 'Spendergarantie',
    countries120: 'Global Reach',
    worldwideReach: 'Weltweite Reichweite',
    raisedIn2024: 'Gesammelt 2024',
    
    // Categories
    browseCategories: 'Finden Sie ein Anliegen das Ihnen wichtig ist',
    browseCategoriesSubtitle: 'Durchsuchen Sie Kampagnen nach Kategorie und unterstützen Sie Anliegen die Ihnen wichtig sind',
    viewAllCategories: 'Alle Kategorien anzeigen',
    
    // Campaign
    donateNow: 'Jetzt spenden',
    shareNow: 'Teilen',
    raised: 'gesammelt',
    goal: 'Ziel',
    donors: 'Spender',
    daysLeft: 'Tage übrig',
    story: 'Geschichte',
    updates: 'Updates',
    donations: 'Spenden',
    milestone: 'Meilenstein',
    
    // AI Features
    aiFeatures: 'KI-Funktionen',
    translateCampaign: 'Übersetzen',
    successPredictor: 'Erfolgsprognose',
    trustScore: 'Vertrauenswert',
    
    // Login/Register
    welcomeBack: 'Willkommen zurück',
    signInToContinue: 'Melden Sie sich an um fortzufahren',
    email: 'E-Mail',
    password: 'Passwort',
    enterEmail: 'E-Mail eingeben',
    enterPassword: 'Passwort eingeben',
    rememberMe: 'Angemeldet bleiben',
    forgotPassword: 'Passwort vergessen?',
    signingIn: 'Anmeldung...',
    noAccount: 'Kein Konto?',
    hasAccount: 'Bereits ein Konto?',
    createAccount: 'Konto erstellen',
    agreeTerms: 'Mit der Anmeldung akzeptieren Sie unsere',
    termsOfService: 'Nutzungsbedingungen',
    privacyPolicy: 'Datenschutzrichtlinie',
    and: 'und',
    
    // Common
    loading: 'Laden...',
    error: 'Fehler',
    submit: 'Absenden',
    cancel: 'Abbrechen',
    save: 'Speichern',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    search: 'Suchen',
    filter: 'Filtern',
    sortBy: 'Sortieren nach',
    of: 'von',
    viewDetails: 'Details anzeigen',
    learnMore: 'Mehr erfahren',
    
    // Featured Campaigns
    featured: 'Empfohlen',
    campaignsMakingImpact: 'Kampagnen mit Wirkung',
    featuredSubtitle: 'Diese verifizierten Kampagnen schaffen echte Veränderung.',
    seeAllCampaigns: 'Alle Kampagnen anzeigen',
    trending: 'Im Trend',
    verified: 'Verifiziert',
    escrow: 'Treuhand',
    supportThisCause: 'Diese Sache unterstützen',
    medical: 'Medizin',
    education: 'Bildung',
    emergency: 'Notfall',
    nonprofit: 'Gemeinnützig',
    sports: 'Sport',
    research: 'Forschung',
    startups: 'Startups',
    community: 'Gemeinschaft',
    whyChooseUs: 'Warum uns wählen',
    mostTrustedPlatform: 'Die vertrauenswürdigste Crowdfunding-Plattform',
    whyChooseSubtitle: 'Wir haben FundTheHope mit Vertrauen und Transparenz gebaut.',
    donorProtection: 'Donor Awareness',
    donorProtectionDesc: 'Make informed donation decisions.',
    securePayments: 'Secure Payments',
    securePaymentsDesc: 'Bank-level encryption protects transactions.',
    verifiedCampaigns: 'Verifizierte Kampagnen',
    verifiedCampaignsDesc: 'Jede Kampagne wird streng überprüft.',
    aiPoweredTools: 'KI-gestützte Tools',
    aiPoweredToolsDesc: 'Intelligente Empfehlungen und Social-Media-Tools.',
    multiLanguageSupport: 'Mehrsprachige Unterstützung',
    multiLanguageSupportDesc: 'Erreichen Sie Spender weltweit.',
    fastFundRelease: 'Schnelle Mittelfreigabe',
    fastFundReleaseDesc: 'Erhalten Sie Mittel in 24-48 Stunden.',
    howWeCompare: 'Wie wir vergleichen',
    feature: 'Funktion',
    otherPlatforms: 'Andere Plattformen',
    protected: 'Geschützt',
    milestoneBased: 'Meilenstein-basiert',
    verification: 'Verifizierung',
    languages: 'Sprachen',
    releaseTime: 'Freigabezeit',
    poweredByAI: 'KI-gestützt',
    superchargeYourFundraising: 'Verbessern Sie Ihr Fundraising mit KI',
    aiSectionSubtitle: 'Unsere KI-Tools helfen Ihnen, überzeugende Kampagnen zu erstellen.',
    aiStoryWriter: 'KI-Geschichtenschreiber',
    aiStoryWriterDesc: 'Erstellen Sie fesselnde Geschichten.',
    aiImageGenerator: 'KI-Bildgenerator',
    aiImageGeneratorDesc: 'Generieren Sie professionelle Bilder.',
    smartRecommendations: 'Intelligente Empfehlungen',
    smartRecommendationsDesc: 'Erhalten Sie personalisierte Vorschläge.',
    socialMediaGenerator: 'Social-Media-Generator',
    socialMediaGeneratorDesc: 'Erstellen Sie ansprechende Posts mit einem Klick.',
    tryItNow: 'Jetzt ausprobieren',
    aiRecommended: 'KI-empfohlen',
    recommendedCampaigns: 'Empfohlene Kampagnen',
    recommendedSubtitle: 'Von unserer KI ausgewählte Kampagnen.',
    viewAll: 'Alle anzeigen',
    howItWorksTitle: 'So funktioniert FundTheHope',
    howItWorksSubtitle: 'Starten Sie in drei einfachen Schritten',
    step1Title: 'Erstellen Sie Ihre Kampagne',
    step1Desc: 'Erzählen Sie Ihre Geschichte und starten Sie in Minuten.',
    step2Title: 'Teilen Sie mit Ihrem Netzwerk',
    step2Desc: 'Verbreiten Sie über Social Media und E-Mail.',
    step3Title: 'Sammeln Sie Spenden',
    step3Desc: 'Empfangen Sie Spenden sicher.',
    getStartedNow: 'Jetzt starten',
    trustedByMillions: 'Von Millionen vertraut',
    trustSectionSubtitle: 'Treten Sie der Community bei, die über 2,5 Mrd. $ gesammelt hat.',
    campaignsFunded: 'Finanzierte Kampagnen',
    countriesSupported: 'Unterstützte Länder',
    donorSatisfaction: 'Spenderzufriedenheit',
    realStoriesRealImpact: 'Echte Geschichten, echte Wirkung',
    testimonialsSubtitle: 'Hören Sie von Menschen, deren Leben sich verändert hat.',
    bankLevelSecurity: 'Banksicherheit',
    securitySubtitle: 'Ihre Spenden und Daten sind geschützt.',
    pciCompliant: 'PCI DSS-konform',
    gdprReady: 'DSGVO-konform',
    ssl256Encryption: '256-Bit SSL-Verschlüsselung',
    readyToMakeDifference: 'Bereit, etwas zu bewirken?',
    ctaSubtitle: 'Starten Sie Ihre Kampagne heute.',
    startFundraising: 'Fundraising starten',
    exploreCampaigns: 'Kampagnen erkunden',
    
    // Footer
    aboutUs: 'Über uns',
    contactUs: 'Kontakt',
    support: 'Support',
    faq: 'FAQ',
    blog: 'Blog',
    careers: 'Karriere',
    press: 'Presse',
    allRightsReserved: 'Alle Rechte vorbehalten',
    quickLinks: 'Schnellzugriff',
    resources: 'Ressourcen',
    legal: 'Rechtliches',
    helpCenter: 'Hilfezentrum',
    safetyTips: 'Sicherheitstipps',
    forCharities: 'Für Wohltätigkeitsorganisationen',
    successGuide: 'Erfolgsratgeber',
    trustAndSafety: 'Vertrauen & Sicherheit',
    cookiePolicy: 'Cookie-Richtlinie',
    footerTagline: 'Menschen befähigen, durch Crowdfunding zu helfen.',
  },
  it: {
    // Header
    discover: 'Scopri',
    howItWorks: 'Come funziona',
    successStories: 'Storie di successo',
    startCampaign: 'Avvia campagna',
    signIn: 'Accedi',
    signUp: 'Registrati',
    dashboard: 'Dashboard',
    profile: 'Profilo',
    settings: 'Impostazioni',
    logout: 'Esci',
    
    // Hero
    heroTitle: 'Raccolta fondi affidabile per',
    heroTitleHighlight: 'tutto ciò che ti sta a cuore',
    heroSubtitle: 'Unisciti a milioni di persone che raccolgono fondi per esigenze mediche, emergenze, organizzazioni no-profit e altro ancora.',
    seeHowItWorks: 'Scopri come funziona',
    searchPlaceholder: 'Cerca campagne...',
    totalRaised: 'Totale raccolto',
    globalDonors: 'Donatori globali',
    successRate: 'Tasso di successo',
    protected100: 'Protected',
    donorGuarantee: 'Garanzia donatore',
    countries120: 'Global Reach',
    worldwideReach: 'Portata mondiale',
    raisedIn2024: 'Raccolto nel 2024',
    
    // Categories
    browseCategories: 'Trova una causa che ti sta a cuore',
    browseCategoriesSubtitle: 'Sfoglia le campagne per categoria e sostieni le cause che contano di più',
    viewAllCategories: 'Vedi tutte le categorie',
    
    // Campaign
    donateNow: 'Dona ora',
    shareNow: 'Condividi',
    raised: 'raccolti',
    goal: 'obiettivo',
    donors: 'donatori',
    daysLeft: 'giorni rimasti',
    story: 'Storia',
    updates: 'Aggiornamenti',
    donations: 'Donazioni',
    milestone: 'Traguardo',
    
    // AI Features
    aiFeatures: 'Funzioni IA',
    translateCampaign: 'Traduci',
    successPredictor: 'Predittore di successo',
    trustScore: 'Punteggio fiducia',
    
    // Login/Register
    welcomeBack: 'Bentornato',
    signInToContinue: 'Accedi per continuare',
    email: 'Email',
    password: 'Password',
    enterEmail: 'Inserisci la tua email',
    enterPassword: 'Inserisci la password',
    rememberMe: 'Ricordami',
    forgotPassword: 'Password dimenticata?',
    signingIn: 'Accesso...',
    noAccount: 'Non hai un account?',
    hasAccount: 'Hai già un account?',
    createAccount: 'Crea account',
    agreeTerms: 'Accedendo, accetti i nostri',
    termsOfService: 'Termini di servizio',
    privacyPolicy: 'Informativa sulla privacy',
    and: 'e',
    
    // Common
    loading: 'Caricamento...',
    error: 'Errore',
    submit: 'Invia',
    cancel: 'Annulla',
    save: 'Salva',
    edit: 'Modifica',
    delete: 'Elimina',
    search: 'Cerca',
    filter: 'Filtra',
    sortBy: 'Ordina per',
    of: 'di',
    viewDetails: 'Vedi dettagli',
    learnMore: 'Scopri di più',
    
    // Featured Campaigns
    featured: 'In evidenza',
    campaignsMakingImpact: 'Campagne che fanno la differenza',
    featuredSubtitle: 'Queste campagne verificate stanno creando un cambiamento reale.',
    seeAllCampaigns: 'Vedi tutte le campagne',
    trending: 'Di tendenza',
    verified: 'Verificato',
    escrow: 'Deposito',
    supportThisCause: 'Sostieni questa causa',
    medical: 'Medico',
    education: 'Istruzione',
    emergency: 'Emergenza',
    nonprofit: 'No profit',
    sports: 'Sport',
    research: 'Ricerca',
    startups: 'Startup',
    community: 'Comunità',
    whyChooseUs: 'Perché sceglierci',
    mostTrustedPlatform: 'La piattaforma di crowdfunding più affidabile',
    whyChooseSubtitle: 'Abbiamo costruito FundTheHope con fiducia e trasparenza.',
    donorProtection: 'Donor Awareness',
    donorProtectionDesc: 'Make informed donation decisions.',
    securePayments: 'Secure Payments',
    securePaymentsDesc: 'Bank-level encryption protects transactions.',
    verifiedCampaigns: 'Campagne verificate',
    verifiedCampaignsDesc: 'Ogni campagna viene verificata rigorosamente.',
    aiPoweredTools: 'Strumenti con IA',
    aiPoweredToolsDesc: 'Raccomandazioni intelligenti e strumenti social.',
    multiLanguageSupport: 'Supporto multilingue',
    multiLanguageSupportDesc: 'Raggiungi donatori in tutto il mondo.',
    fastFundRelease: 'Rilascio rapido dei fondi',
    fastFundReleaseDesc: 'Ricevi i fondi in 24-48 ore.',
    howWeCompare: 'Come ci confrontiamo',
    feature: 'Funzionalità',
    otherPlatforms: 'Altre piattaforme',
    protected: 'Protetto',
    milestoneBased: 'Basato su traguardi',
    verification: 'Verifica',
    languages: 'Lingue',
    releaseTime: 'Tempo di rilascio',
    poweredByAI: 'Alimentato da IA',
    superchargeYourFundraising: 'Potenzia la tua raccolta fondi con l\'IA',
    aiSectionSubtitle: 'I nostri strumenti IA ti aiutano a creare campagne efficaci.',
    aiStoryWriter: 'Scrittore di storie IA',
    aiStoryWriterDesc: 'Crea storie che connettono con i donatori.',
    aiImageGenerator: 'Generatore di immagini IA',
    aiImageGeneratorDesc: 'Genera immagini professionali.',
    smartRecommendations: 'Raccomandazioni intelligenti',
    smartRecommendationsDesc: 'Ottieni suggerimenti personalizzati.',
    socialMediaGenerator: 'Generatore social media',
    socialMediaGeneratorDesc: 'Crea post coinvolgenti con un clic.',
    tryItNow: 'Prova ora',
    aiRecommended: 'Raccomandato da IA',
    recommendedCampaigns: 'Campagne raccomandate',
    recommendedSubtitle: 'Campagne selezionate dalla nostra IA.',
    viewAll: 'Vedi tutto',
    howItWorksTitle: 'Come funziona FundTheHope',
    howItWorksSubtitle: 'Inizia in tre semplici passaggi',
    step1Title: 'Crea la tua campagna',
    step1Desc: 'Racconta la tua storia e lancia in pochi minuti.',
    step2Title: 'Condividi con la tua rete',
    step2Desc: 'Diffondi tramite social media ed email.',
    step3Title: 'Raccogli donazioni',
    step3Desc: 'Ricevi donazioni in modo sicuro.',
    getStartedNow: 'Inizia ora',
    trustedByMillions: 'Fidato da milioni',
    trustSectionSubtitle: 'Unisciti alla community che ha raccolto oltre 2,5 miliardi $.',
    campaignsFunded: 'Campagne finanziate',
    countriesSupported: 'Paesi supportati',
    donorSatisfaction: 'Soddisfazione donatori',
    realStoriesRealImpact: 'Storie vere, impatto reale',
    testimonialsSubtitle: 'Ascolta chi ha visto la propria vita cambiare.',
    bankLevelSecurity: 'Sicurezza bancaria',
    securitySubtitle: 'Le tue donazioni e i dati sono protetti.',
    pciCompliant: 'Conforme PCI DSS',
    gdprReady: 'Pronto GDPR',
    ssl256Encryption: 'Crittografia SSL a 256 bit',
    readyToMakeDifference: 'Pronto a fare la differenza?',
    ctaSubtitle: 'Inizia la tua campagna oggi.',
    startFundraising: 'Inizia la raccolta',
    exploreCampaigns: 'Esplora le campagne',
    
    // Footer
    aboutUs: 'Chi siamo',
    contactUs: 'Contattaci',
    support: 'Supporto',
    faq: 'FAQ',
    blog: 'Blog',
    careers: 'Carriere',
    press: 'Stampa',
    allRightsReserved: 'Tutti i diritti riservati',
    quickLinks: 'Link rapidi',
    resources: 'Risorse',
    legal: 'Legale',
    helpCenter: 'Centro assistenza',
    safetyTips: 'Consigli di sicurezza',
    forCharities: 'Per le organizzazioni',
    successGuide: 'Guida al successo',
    trustAndSafety: 'Fiducia e Sicurezza',
    cookiePolicy: 'Politica sui cookie',
    footerTagline: 'Permettere alle persone di aiutarsi attraverso il crowdfunding.',
  },
  // Portuguese
  pt: {
    discover: 'Descobrir', howItWorks: 'Como Funciona', successStories: 'Histórias de Sucesso',
    startCampaign: 'Iniciar Campanha', signIn: 'Entrar', signUp: 'Registrar',
    dashboard: 'Painel', profile: 'Perfil', settings: 'Configurações', logout: 'Sair',
    heroTitle: 'Arrecadação de fundos confiável para', heroTitleHighlight: 'tudo o que você se importa',
    heroSubtitle: 'Junte-se a milhões que arrecadam dinheiro para necessidades médicas, emergências e muito mais.',
    donateNow: 'Doar Agora', shareNow: 'Compartilhar', raised: 'arrecadado', goal: 'meta',
    donors: 'doadores', daysLeft: 'dias restantes', loading: 'Carregando...',
    totalRaised: 'Total Arrecadado', globalDonors: 'Doadores Globais', successRate: 'Taxa de Sucesso',
    footerTagline: 'Capacitando pessoas a ajudar pessoas através de crowdfunding confiável.',
  },
  // Dutch
  nl: {
    discover: 'Ontdekken', howItWorks: 'Hoe Het Werkt', successStories: 'Succesverhalen',
    startCampaign: 'Start Campagne', signIn: 'Inloggen', signUp: 'Registreren',
    dashboard: 'Dashboard', profile: 'Profiel', settings: 'Instellingen', logout: 'Uitloggen',
    heroTitle: 'Betrouwbare fondsenwerving voor', heroTitleHighlight: 'alles waar je om geeft',
    heroSubtitle: 'Sluit je aan bij miljoenen die geld inzamelen voor medische behoeften, noodgevallen en meer.',
    donateNow: 'Nu Doneren', shareNow: 'Delen', raised: 'opgehaald', goal: 'doel',
    donors: 'donateurs', daysLeft: 'dagen over', loading: 'Laden...',
    totalRaised: 'Totaal Opgehaald', globalDonors: 'Wereldwijde Donateurs', successRate: 'Succespercentage',
    footerTagline: 'Mensen helpen elkaar te helpen via betrouwbare crowdfunding.',
  },
  // Polish
  pl: {
    discover: 'Odkryj', howItWorks: 'Jak To Działa', successStories: 'Historie Sukcesu',
    startCampaign: 'Rozpocznij Kampanię', signIn: 'Zaloguj', signUp: 'Zarejestruj',
    dashboard: 'Panel', profile: 'Profil', settings: 'Ustawienia', logout: 'Wyloguj',
    heroTitle: 'Zaufana zbiórka pieniędzy na', heroTitleHighlight: 'wszystko, na czym ci zależy',
    heroSubtitle: 'Dołącz do milionów zbierających pieniądze na potrzeby medyczne, nagłe wypadki i więcej.',
    donateNow: 'Wpłać Teraz', shareNow: 'Udostępnij', raised: 'zebrano', goal: 'cel',
    donors: 'darczyńcy', daysLeft: 'dni pozostało', loading: 'Ładowanie...',
    totalRaised: 'Łącznie Zebrano', globalDonors: 'Globalni Darczyńcy', successRate: 'Wskaźnik Sukcesu',
    footerTagline: 'Umożliwianie ludziom pomagania sobie nawzajem poprzez zaufany crowdfunding.',
  },
  // Russian
  ru: {
    discover: 'Исследовать', howItWorks: 'Как Это Работает', successStories: 'Истории Успеха',
    startCampaign: 'Начать Кампанию', signIn: 'Войти', signUp: 'Регистрация',
    dashboard: 'Панель', profile: 'Профиль', settings: 'Настройки', logout: 'Выйти',
    heroTitle: 'Надежный сбор средств для', heroTitleHighlight: 'всего, что вам дорого',
    heroSubtitle: 'Присоединяйтесь к миллионам, собирающим средства на медицинские нужды, экстренные ситуации и многое другое.',
    donateNow: 'Пожертвовать', shareNow: 'Поделиться', raised: 'собрано', goal: 'цель',
    donors: 'доноры', daysLeft: 'дней осталось', loading: 'Загрузка...',
    totalRaised: 'Всего Собрано', globalDonors: 'Глобальные Доноры', successRate: 'Успешность',
    footerTagline: 'Помогаем людям помогать друг другу через надежный краудфандинг.',
  },
  // Ukrainian
  uk: {
    discover: 'Відкрити', howItWorks: 'Як Це Працює', successStories: 'Історії Успіху',
    startCampaign: 'Почати Кампанію', signIn: 'Увійти', signUp: 'Реєстрація',
    dashboard: 'Панель', profile: 'Профіль', settings: 'Налаштування', logout: 'Вийти',
    heroTitle: 'Надійний збір коштів для', heroTitleHighlight: 'всього, що вам важливо',
    heroSubtitle: 'Приєднуйтесь до мільйонів, які збирають кошти на медичні потреби, надзвичайні ситуації та інше.',
    donateNow: 'Пожертвувати', shareNow: 'Поділитися', raised: 'зібрано', goal: 'мета',
    donors: 'донори', daysLeft: 'днів залишилось', loading: 'Завантаження...',
    totalRaised: 'Всього Зібрано', globalDonors: 'Глобальні Донори', successRate: 'Успішність',
    footerTagline: 'Допомагаємо людям допомагати одне одному через надійний краудфандинг.',
  },
  // Swedish
  sv: {
    discover: 'Utforska', howItWorks: 'Hur Det Fungerar', successStories: 'Framgångshistorier',
    startCampaign: 'Starta Kampanj', signIn: 'Logga In', signUp: 'Registrera',
    dashboard: 'Översikt', profile: 'Profil', settings: 'Inställningar', logout: 'Logga Ut',
    heroTitle: 'Pålitlig insamling för', heroTitleHighlight: 'allt du bryr dig om',
    heroSubtitle: 'Gå med miljoner som samlar in pengar för medicinska behov, nödsituationer och mer.',
    donateNow: 'Donera Nu', shareNow: 'Dela', raised: 'insamlat', goal: 'mål',
    donors: 'givare', daysLeft: 'dagar kvar', loading: 'Laddar...',
    totalRaised: 'Totalt Insamlat', globalDonors: 'Globala Givare', successRate: 'Framgångsgrad',
    footerTagline: 'Hjälper människor att hjälpa varandra genom pålitlig crowdfunding.',
  },
  // Norwegian
  no: {
    discover: 'Utforsk', howItWorks: 'Hvordan Det Fungerer', successStories: 'Suksesshistorier',
    startCampaign: 'Start Kampanje', signIn: 'Logg Inn', signUp: 'Registrer',
    dashboard: 'Oversikt', profile: 'Profil', settings: 'Innstillinger', logout: 'Logg Ut',
    heroTitle: 'Pålitelig innsamling for', heroTitleHighlight: 'alt du bryr deg om',
    heroSubtitle: 'Join our community raising money for medical needs, emergencies, and more.',
    donateNow: 'Doner Nå', shareNow: 'Del', raised: 'samlet inn', goal: 'mål',
    donors: 'givere', daysLeft: 'dager igjen', loading: 'Laster...',
    totalRaised: 'Totalt Samlet Inn', globalDonors: 'Globale Givere', successRate: 'Suksessrate',
    footerTagline: 'Hjelper folk å hjelpe hverandre gjennom pålitelig crowdfunding.',
  },
  // Danish
  da: {
    discover: 'Udforsk', howItWorks: 'Sådan Fungerer Det', successStories: 'Succeshistorier',
    startCampaign: 'Start Kampagne', signIn: 'Log Ind', signUp: 'Tilmeld',
    dashboard: 'Oversigt', profile: 'Profil', settings: 'Indstillinger', logout: 'Log Ud',
    heroTitle: 'Pålidelig fundraising til', heroTitleHighlight: 'alt du holder af',
    heroSubtitle: 'Join our community raising money for medical needs, emergencies, and more.',
    donateNow: 'Doner Nu', shareNow: 'Del', raised: 'indsamlet', goal: 'mål',
    donors: 'donorer', daysLeft: 'dage tilbage', loading: 'Indlæser...',
    totalRaised: 'Samlet Indsamlet', globalDonors: 'Globale Donorer', successRate: 'Succesrate',
    footerTagline: 'Hjælper folk med at hjælpe hinanden gennem pålidelig crowdfunding.',
  },
  // Finnish
  fi: {
    discover: 'Tutustu', howItWorks: 'Miten Se Toimii', successStories: 'Menestystarinat',
    startCampaign: 'Aloita Kampanja', signIn: 'Kirjaudu', signUp: 'Rekisteröidy',
    dashboard: 'Hallintapaneeli', profile: 'Profiili', settings: 'Asetukset', logout: 'Kirjaudu Ulos',
    heroTitle: 'Luotettava varainkeruu', heroTitleHighlight: 'kaikelle, mistä välität',
    heroSubtitle: 'Liity miljooniin, jotka keräävät rahaa lääketieteellisiin tarpeisiin, hätätilanteisiin ja muuhun.',
    donateNow: 'Lahjoita Nyt', shareNow: 'Jaa', raised: 'kerätty', goal: 'tavoite',
    donors: 'lahjoittajat', daysLeft: 'päivää jäljellä', loading: 'Ladataan...',
    totalRaised: 'Kerätty Yhteensä', globalDonors: 'Globaalit Lahjoittajat', successRate: 'Onnistumisprosentti',
    footerTagline: 'Autamme ihmisiä auttamaan toisiaan luotettavan joukkorahoituksen kautta.',
  },
  // Greek
  el: {
    discover: 'Ανακαλύψτε', howItWorks: 'Πώς Λειτουργεί', successStories: 'Ιστορίες Επιτυχίας',
    startCampaign: 'Ξεκινήστε Καμπάνια', signIn: 'Σύνδεση', signUp: 'Εγγραφή',
    dashboard: 'Πίνακας', profile: 'Προφίλ', settings: 'Ρυθμίσεις', logout: 'Αποσύνδεση',
    heroTitle: 'Αξιόπιστη συγκέντρωση χρημάτων για', heroTitleHighlight: 'ό,τι σας ενδιαφέρει',
    heroSubtitle: 'Γίνετε μέλος εκατομμυρίων που συγκεντρώνουν χρήματα για ιατρικές ανάγκες, έκτακτες ανάγκες και άλλα.',
    donateNow: 'Δωρίστε Τώρα', shareNow: 'Μοιραστείτε', raised: 'συγκεντρώθηκαν', goal: 'στόχος',
    donors: 'δωρητές', daysLeft: 'μέρες απομένουν', loading: 'Φόρτωση...',
    totalRaised: 'Συνολικά Συγκεντρώθηκαν', globalDonors: 'Παγκόσμιοι Δωρητές', successRate: 'Ποσοστό Επιτυχίας',
    footerTagline: 'Δίνοντας τη δυνατότητα στους ανθρώπους να βοηθούν ο ένας τον άλλον.',
  },
  // Czech
  cs: {
    discover: 'Objevit', howItWorks: 'Jak To Funguje', successStories: 'Příběhy Úspěchu',
    startCampaign: 'Zahájit Kampaň', signIn: 'Přihlásit', signUp: 'Registrovat',
    dashboard: 'Přehled', profile: 'Profil', settings: 'Nastavení', logout: 'Odhlásit',
    heroTitle: 'Důvěryhodný fundraising pro', heroTitleHighlight: 'vše, na čem vám záleží',
    heroSubtitle: 'Připojte se k milionům, kteří sbírají peníze na zdravotní potřeby, mimořádné situace a další.',
    donateNow: 'Přispět', shareNow: 'Sdílet', raised: 'vybráno', goal: 'cíl',
    donors: 'dárci', daysLeft: 'dní zbývá', loading: 'Načítání...',
    totalRaised: 'Celkem Vybráno', globalDonors: 'Globální Dárci', successRate: 'Úspěšnost',
    footerTagline: 'Pomáháme lidem pomáhat si navzájem prostřednictvím důvěryhodného crowdfundingu.',
  },
  // Romanian
  ro: {
    discover: 'Descoperă', howItWorks: 'Cum Funcționează', successStories: 'Povești de Succes',
    startCampaign: 'Începe Campanie', signIn: 'Conectare', signUp: 'Înregistrare',
    dashboard: 'Panou', profile: 'Profil', settings: 'Setări', logout: 'Deconectare',
    heroTitle: 'Strângere de fonduri de încredere pentru', heroTitleHighlight: 'tot ce îți pasă',
    heroSubtitle: 'Alătură-te milioanelor care strâng bani pentru nevoi medicale, urgențe și multe altele.',
    donateNow: 'Donează Acum', shareNow: 'Distribuie', raised: 'strâns', goal: 'țintă',
    donors: 'donatori', daysLeft: 'zile rămase', loading: 'Se încarcă...',
    totalRaised: 'Total Strâns', globalDonors: 'Donatori Globali', successRate: 'Rata de Succes',
    footerTagline: 'Ajutăm oamenii să se ajute reciproc prin crowdfunding de încredere.',
  },
  // Hungarian
  hu: {
    discover: 'Felfedezés', howItWorks: 'Hogyan Működik', successStories: 'Sikertörténetek',
    startCampaign: 'Kampány Indítása', signIn: 'Bejelentkezés', signUp: 'Regisztráció',
    dashboard: 'Vezérlőpult', profile: 'Profil', settings: 'Beállítások', logout: 'Kijelentkezés',
    heroTitle: 'Megbízható adománygyűjtés', heroTitleHighlight: 'mindenre, ami fontos',
    heroSubtitle: 'Csatlakozz milliókhoz, akik pénzt gyűjtenek orvosi szükségletekre, vészhelyzetekre és egyebekre.',
    donateNow: 'Adományozás', shareNow: 'Megosztás', raised: 'összegyűjtve', goal: 'cél',
    donors: 'adományozók', daysLeft: 'nap van hátra', loading: 'Betöltés...',
    totalRaised: 'Összesen Gyűjtött', globalDonors: 'Globális Adományozók', successRate: 'Sikerráta',
    footerTagline: 'Segítünk az embereknek egymást segíteni megbízható közösségi finanszírozáson keresztül.',
  },
  // Turkish
  tr: {
    discover: 'Keşfet', howItWorks: 'Nasıl Çalışır', successStories: 'Başarı Hikayeleri',
    startCampaign: 'Kampanya Başlat', signIn: 'Giriş Yap', signUp: 'Kayıt Ol',
    dashboard: 'Panel', profile: 'Profil', settings: 'Ayarlar', logout: 'Çıkış',
    heroTitle: 'Güvenilir bağış toplama', heroTitleHighlight: 'önemsediğiniz her şey için',
    heroSubtitle: 'Tıbbi ihtiyaçlar, acil durumlar ve daha fazlası için para toplayan milyonlara katılın.',
    donateNow: 'Şimdi Bağış Yap', shareNow: 'Paylaş', raised: 'toplandı', goal: 'hedef',
    donors: 'bağışçılar', daysLeft: 'gün kaldı', loading: 'Yükleniyor...',
    totalRaised: 'Toplam Toplanan', globalDonors: 'Global Bağışçılar', successRate: 'Başarı Oranı',
    footerTagline: 'Güvenilir kitle fonlaması ile insanların birbirine yardım etmesini sağlıyoruz.',
  },
  // Japanese
  ja: {
    discover: '探す', howItWorks: '仕組み', successStories: '成功事例',
    startCampaign: 'キャンペーン開始', signIn: 'ログイン', signUp: '新規登録',
    dashboard: 'ダッシュボード', profile: 'プロフィール', settings: '設定', logout: 'ログアウト',
    heroTitle: '信頼できる募金活動', heroTitleHighlight: 'あなたが大切にするすべてのために',
    heroSubtitle: '医療費、緊急事態などのために資金を集める何百万人もの仲間に加わりましょう。',
    donateNow: '今すぐ寄付', shareNow: 'シェア', raised: '集まった', goal: '目標',
    donors: '寄付者', daysLeft: '日残り', loading: '読み込み中...',
    totalRaised: '総額', globalDonors: 'グローバル寄付者', successRate: '成功率',
    footerTagline: '信頼できるクラウドファンディングを通じて人々が助け合えるようにします。',
  },
  // Korean
  ko: {
    discover: '발견하기', howItWorks: '작동 방식', successStories: '성공 사례',
    startCampaign: '캠페인 시작', signIn: '로그인', signUp: '회원가입',
    dashboard: '대시보드', profile: '프로필', settings: '설정', logout: '로그아웃',
    heroTitle: '신뢰할 수 있는 모금 활동', heroTitleHighlight: '당신이 관심 있는 모든 것을 위해',
    heroSubtitle: '의료비, 긴급 상황 등을 위해 모금하는 수백만 명과 함께하세요.',
    donateNow: '지금 기부', shareNow: '공유', raised: '모금됨', goal: '목표',
    donors: '기부자', daysLeft: '일 남음', loading: '로딩 중...',
    totalRaised: '총 모금액', globalDonors: '글로벌 기부자', successRate: '성공률',
    footerTagline: '신뢰할 수 있는 크라우드펀딩을 통해 사람들이 서로 돕도록 합니다.',
  },
  // Hindi
  hi: {
    discover: 'खोजें', howItWorks: 'कैसे काम करता है', successStories: 'सफलता की कहानियां',
    startCampaign: 'अभियान शुरू करें', signIn: 'साइन इन', signUp: 'साइन अप',
    dashboard: 'डैशबोर्ड', profile: 'प्रोफाइल', settings: 'सेटिंग्स', logout: 'लॉग आउट',
    heroTitle: 'विश्वसनीय धन उगाहना', heroTitleHighlight: 'जो आपको प्रिय है उसके लिए',
    heroSubtitle: 'चिकित्सा जरूरतों, आपात स्थितियों और बहुत कुछ के लिए धन जुटाने वाले लाखों लोगों से जुड़ें।',
    donateNow: 'अभी दान करें', shareNow: 'शेयर करें', raised: 'जुटाया', goal: 'लक्ष्य',
    donors: 'दानदाता', daysLeft: 'दिन बाकी', loading: 'लोड हो रहा है...',
    totalRaised: 'कुल जुटाया', globalDonors: 'वैश्विक दानदाता', successRate: 'सफलता दर',
    footerTagline: 'विश्वसनीय क्राउडफंडिंग के माध्यम से लोगों को एक दूसरे की मदद करने में सक्षम बनाना।',
  },
  // Thai
  th: {
    discover: 'ค้นพบ', howItWorks: 'วิธีการทำงาน', successStories: 'เรื่องราวความสำเร็จ',
    startCampaign: 'เริ่มแคมเปญ', signIn: 'เข้าสู่ระบบ', signUp: 'ลงทะเบียน',
    dashboard: 'แดชบอร์ด', profile: 'โปรไฟล์', settings: 'การตั้งค่า', logout: 'ออกจากระบบ',
    heroTitle: 'การระดมทุนที่เชื่อถือได้สำหรับ', heroTitleHighlight: 'ทุกสิ่งที่คุณใส่ใจ',
    heroSubtitle: 'เข้าร่วมกับผู้คนนับล้านที่ระดมเงินเพื่อความต้องการทางการแพทย์ เหตุฉุกเฉิน และอื่นๆ',
    donateNow: 'บริจาคเลย', shareNow: 'แชร์', raised: 'ระดมได้', goal: 'เป้าหมาย',
    donors: 'ผู้บริจาค', daysLeft: 'วันที่เหลือ', loading: 'กำลังโหลด...',
    totalRaised: 'ยอดรวมที่ระดมได้', globalDonors: 'ผู้บริจาคทั่วโลก', successRate: 'อัตราความสำเร็จ',
    footerTagline: 'เสริมพลังให้ผู้คนช่วยเหลือกันผ่านการระดมทุนที่เชื่อถือได้',
  },
  // Vietnamese
  vi: {
    discover: 'Khám phá', howItWorks: 'Cách hoạt động', successStories: 'Câu chuyện thành công',
    startCampaign: 'Bắt đầu chiến dịch', signIn: 'Đăng nhập', signUp: 'Đăng ký',
    dashboard: 'Bảng điều khiển', profile: 'Hồ sơ', settings: 'Cài đặt', logout: 'Đăng xuất',
    heroTitle: 'Gây quỹ đáng tin cậy cho', heroTitleHighlight: 'mọi thứ bạn quan tâm',
    heroSubtitle: 'Tham gia cùng hàng triệu người đang quyên góp tiền cho nhu cầu y tế, tình huống khẩn cấp và hơn thế nữa.',
    donateNow: 'Quyên góp ngay', shareNow: 'Chia sẻ', raised: 'đã quyên góp', goal: 'mục tiêu',
    donors: 'nhà hảo tâm', daysLeft: 'ngày còn lại', loading: 'Đang tải...',
    totalRaised: 'Tổng số quyên góp', globalDonors: 'Nhà hảo tâm toàn cầu', successRate: 'Tỷ lệ thành công',
    footerTagline: 'Trao quyền cho mọi người giúp đỡ nhau thông qua gây quỹ cộng đồng đáng tin cậy.',
  },
  // Indonesian
  id: {
    discover: 'Jelajahi', howItWorks: 'Cara Kerja', successStories: 'Kisah Sukses',
    startCampaign: 'Mulai Kampanye', signIn: 'Masuk', signUp: 'Daftar',
    dashboard: 'Dasbor', profile: 'Profil', settings: 'Pengaturan', logout: 'Keluar',
    heroTitle: 'Penggalangan dana terpercaya untuk', heroTitleHighlight: 'semua yang Anda pedulikan',
    heroSubtitle: 'Bergabunglah dengan jutaan orang yang mengumpulkan dana untuk kebutuhan medis, keadaan darurat, dan lainnya.',
    donateNow: 'Donasi Sekarang', shareNow: 'Bagikan', raised: 'terkumpul', goal: 'target',
    donors: 'donatur', daysLeft: 'hari tersisa', loading: 'Memuat...',
    totalRaised: 'Total Terkumpul', globalDonors: 'Donatur Global', successRate: 'Tingkat Keberhasilan',
    footerTagline: 'Memberdayakan orang untuk saling membantu melalui crowdfunding terpercaya.',
  },
  // Arabic
  ar: {
    discover: 'اكتشف', howItWorks: 'كيف يعمل', successStories: 'قصص النجاح',
    startCampaign: 'ابدأ حملة', signIn: 'تسجيل الدخول', signUp: 'إنشاء حساب',
    dashboard: 'لوحة التحكم', profile: 'الملف الشخصي', settings: 'الإعدادات', logout: 'تسجيل الخروج',
    heroTitle: 'جمع تبرعات موثوق لـ', heroTitleHighlight: 'كل ما يهمك',
    heroSubtitle: 'انضم إلى الملايين الذين يجمعون الأموال للاحتياجات الطبية وحالات الطوارئ والمزيد.',
    donateNow: 'تبرع الآن', shareNow: 'شارك', raised: 'تم جمعه', goal: 'الهدف',
    donors: 'المتبرعون', daysLeft: 'أيام متبقية', loading: 'جاري التحميل...',
    totalRaised: 'إجمالي التبرعات', globalDonors: 'المتبرعون العالميون', successRate: 'معدل النجاح',
    footerTagline: 'تمكين الناس من مساعدة بعضهم البعض من خلال التمويل الجماعي الموثوق.',
  },
  // Bengali
  bn: {
    discover: 'অন্বেষণ করুন', howItWorks: 'কিভাবে কাজ করে', successStories: 'সাফল্যের গল্প',
    startCampaign: 'প্রচারণা শুরু করুন', signIn: 'সাইন ইন', signUp: 'সাইন আপ',
    dashboard: 'ড্যাশবোর্ড', profile: 'প্রোফাইল', settings: 'সেটিংস', logout: 'লগ আউট',
    heroTitle: 'বিশ্বস্ত তহবিল সংগ্রহ', heroTitleHighlight: 'আপনার যত্নশীল সবকিছুর জন্য',
    heroSubtitle: 'চিকিৎসা প্রয়োজন, জরুরি অবস্থা এবং আরও অনেক কিছুর জন্য অর্থ সংগ্রহকারী লক্ষ লক্ষ মানুষের সাথে যোগ দিন।',
    donateNow: 'এখনই দান করুন', shareNow: 'শেয়ার করুন', raised: 'সংগৃহীত', goal: 'লক্ষ্য',
    donors: 'দাতা', daysLeft: 'দিন বাকি', loading: 'লোড হচ্ছে...',
    totalRaised: 'মোট সংগৃহীত', globalDonors: 'বৈশ্বিক দাতা', successRate: 'সাফল্যের হার',
    footerTagline: 'বিশ্বস্ত ক্রাউডফান্ডিংয়ের মাধ্যমে মানুষকে একে অপরকে সাহায্য করতে সক্ষম করা।',
  },
  // Filipino
  fil: {
    discover: 'Tuklasin', howItWorks: 'Paano Gumagana', successStories: 'Mga Kwento ng Tagumpay',
    startCampaign: 'Magsimula ng Kampanya', signIn: 'Mag-sign In', signUp: 'Mag-sign Up',
    dashboard: 'Dashboard', profile: 'Profile', settings: 'Mga Setting', logout: 'Mag-log Out',
    heroTitle: 'Mapagkakatiwalaang fundraising para sa', heroTitleHighlight: 'lahat ng mahalaga sa iyo',
    heroSubtitle: 'Sumali sa milyun-milyong nangangalap ng pondo para sa mga medikal na pangangailangan, emergency, at higit pa.',
    donateNow: 'Mag-donate Ngayon', shareNow: 'I-share', raised: 'nakalap', goal: 'layunin',
    donors: 'mga donor', daysLeft: 'araw na natitira', loading: 'Naglo-load...',
    totalRaised: 'Kabuuang Nakalap', globalDonors: 'Mga Global na Donor', successRate: 'Rate ng Tagumpay',
    footerTagline: 'Binibigyang-kakayahan ang mga tao na tulungan ang isa\'t isa sa pamamagitan ng mapagkakatiwalaang crowdfunding.',
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Priority: 1) Saved preference, 2) Browser language, 3) Default 'en'
    const saved = localStorage.getItem('fundthehope_language');
    if (saved && languages.find(l => l.code === saved)) {
      return saved;
    }
    // Auto-detect from browser
    return detectBrowserLanguage();
  });

  useEffect(() => {
    // Save language preference
    localStorage.setItem('fundthehope_language', language);
    // Update document lang attribute
    document.documentElement.lang = language;
    // Set text direction for RTL languages (Arabic)
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const t = (key) => {
    // Try current language, fall back to English, then return key
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const currentLanguage = languages.find(l => l.code === language) || languages[0];

  // Group languages by region for dropdown
  const languagesByRegion = {
    global: languages.filter(l => l.region === 'global'),
    eu: languages.filter(l => l.region === 'eu'),
    asia: languages.filter(l => l.region === 'asia'),
  };

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      t, 
      currentLanguage,
      languages,
      languagesByRegion,
      detectBrowserLanguage 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
